/*
  Custom sections (te-*)

  Behaviour for sections and components added on top of Prestige. Plain JavaScript, loaded with defer after
  theme.js. Every component re-initialises on shopify:section:load so it keeps working in the theme editor.
*/

(function () {
  'use strict';

  /* ---------------------------------------------------------------------------
     Sticky Add to Cart bar (snippets/te-sticky-atc.liquid)
     --------------------------------------------------------------------------- */

  function findByValue(elements, getValue, value) {
    for (var i = 0; i < elements.length; i++) {
      if (getValue(elements[i]) === value) {
        return elements[i];
      }
    }

    return null;
  }

  // Select every option of the variant in the main product form, the same way a shopper would, so the theme
  // updates prices, stock and the Add to Cart button as usual
  function selectVariantInMainForm(productSection, product, variantId) {
    var variant = findByValue(product.variants, function (item) { return String(item.id); }, variantId);

    if (!variant) {
      return;
    }

    [1, 2, 3].forEach(function (position) {
      var value = variant['option' + position];

      if (value === null || value === undefined) {
        return;
      }

      // Colour/size swatches are radio buttons
      var radio = findByValue(
        productSection.querySelectorAll('.ProductForm__Option [type="radio"][data-option-position="' + position + '"]'),
        function (element) { return element.value; },
        value
      );

      if (radio) {
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }

        return;
      }

      // Other options are lists opened from a button (the theme's popovers)
      var popoverValue = findByValue(
        productSection.querySelectorAll('.OptionSelector [data-action="select-value"][data-option-position="' + position + '"]'),
        function (element) { return element.getAttribute('data-value'); },
        value
      );

      if (popoverValue && !popoverValue.classList.contains('is-selected')) {
        popoverValue.click();
      }
    });
  }

  function initStickyAddToCart(bar) {
    if (bar.hasAttribute('data-te-initialized')) {
      return;
    }

    var productSection = bar.parentElement.querySelector('[data-section-type="product"]');
    var mainButton = productSection && productSection.querySelector('.ProductForm__AddToCart');
    var productJson = productSection && productSection.querySelector('[data-product-json]');

    if (!mainButton || !productJson || !('IntersectionObserver' in window)) {
      return;
    }

    var product = JSON.parse(productJson.innerHTML).product;
    var mainPrice = productSection.querySelector('.ProductMeta__PriceList');
    var stickyPrice = bar.querySelector('[data-te-sticky-atc-price]');
    var stickyButton = bar.querySelector('[data-te-sticky-atc-button]');
    var variantSelect = bar.querySelector('[data-te-sticky-atc-variant]');
    var variantRow = bar.querySelector('[data-te-sticky-atc-variants]');
    var variantButtons = bar.querySelectorAll('[data-te-sticky-atc-variant-button]');

    // theme.js REPLACES the main Add to Cart button on every variant change (and can do so while the page sets up),
    // so the button is looked up each time, and the bar watches its wrapper (the buy row), which stays in place
    function getMainButton() {
      return productSection.querySelector('.ProductForm__AddToCart') || mainButton;
    }

    var watchedElement = mainButton.closest('.te-buy-row') || mainButton;

    bar.setAttribute('data-te-initialized', '');
    document.documentElement.classList.add('te-has-sticky-atc');

    // Show the bar only once the main Add to Cart button has scrolled up and out of view
    function updateVisibility() {
      var rect = watchedElement.getBoundingClientRect();
      var isVisible = rect.height > 0 && rect.bottom <= 0;

      bar.classList.toggle('is-visible', isVisible);
      bar.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
    }

    new IntersectionObserver(updateVisibility).observe(watchedElement);

    // The observer only reports a change. When the page lands below the button without scrolling through it (a jump
    // to the bottom, a position restored on Back or reload), nothing changes for it, so scrolling checks as well.
    var visibilityFrame = 0;

    window.addEventListener('scroll', function () {
      if (visibilityFrame) {
        return;
      }

      visibilityFrame = window.requestAnimationFrame(function () {
        visibilityFrame = 0;
        updateVisibility();
      });
    }, { passive: true });

    stickyButton.addEventListener('click', function () {
      getMainButton().click();
    });

    // Boxes/pills: when the variant buttons don't fit the bar (many variants, long names, small screens), the
    // dropdown rendered next to them takes their place. Measured with the buttons shown, again on every width change.
    if (variantRow) {
      var lastWidth = 0;

      var updateCompact = function () {
        var width = bar.clientWidth;

        if (width === lastWidth) {
          return;
        }

        lastWidth = width;
        bar.classList.remove('te-sticky-atc--compact');
        bar.classList.toggle('te-sticky-atc--compact', variantRow.scrollWidth > variantRow.clientWidth + 1);
      };

      updateCompact();

      if ('ResizeObserver' in window) {
        new ResizeObserver(updateCompact).observe(bar);
      } else {
        window.addEventListener('resize', updateCompact);
      }

      // Button widths change once the web font arrives
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () {
          lastWidth = 0;
          updateCompact();
        });
      }
    }

    if (variantSelect) {
      variantSelect.addEventListener('change', function () {
        selectVariantInMainForm(productSection, product, variantSelect.value);
      });
    }

    // Boxes and pills styles show the variants as buttons
    variantButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        selectVariantInMainForm(productSection, product, button.getAttribute('data-variant-id'));
      });
    });

    // Mirror the main form whenever its variant changes. The theme has already updated (and currency-converted)
    // the main price and button by the time this event fires.
    productSection.addEventListener('variant:changed', function (event) {
      var variant = event.detail.variant;

      var currentButton = getMainButton();

      if (mainPrice) {
        stickyPrice.innerHTML = mainPrice.innerHTML;
      }

      stickyButton.innerHTML = currentButton.innerHTML;
      stickyButton.disabled = currentButton.disabled;

      if (!variant) {
        return;
      }

      if (variantSelect) {
        variantSelect.value = variant.id;
      }

      variantButtons.forEach(function (button) {
        button.setAttribute('aria-pressed', String(button.getAttribute('data-variant-id') === String(variant.id)));
      });
    });
  }

  /* ---------------------------------------------------------------------------
     Sold-out option values in product forms

     Strikes through sizes/colours that are sold out, given the other options already chosen. They stay
     selectable so shoppers still see the "Sold out" state and can sign up for back-in-stock alerts.
     --------------------------------------------------------------------------- */

  var OPTION_VALUES = '.ProductForm__Option [type="radio"][data-option-position], .OptionSelector [data-action="select-value"][data-option-position]';

  function getOptionValue(element) {
    return element.tagName === 'INPUT' ? element.value : element.getAttribute('data-value');
  }

  // The option values currently chosen in the form, by position (1 to 3)
  function getSelectedOptions(productSection) {
    var selected = {};

    productSection.querySelectorAll(OPTION_VALUES).forEach(function (element) {
      if (element.checked || element.classList.contains('is-selected')) {
        selected[element.getAttribute('data-option-position')] = getOptionValue(element);
      }
    });

    return selected;
  }

  function isOptionValueAvailable(product, selected, position, value) {
    return product.variants.some(function (variant) {
      if (!variant.available || variant['option' + position] !== value) {
        return false;
      }

      return [1, 2, 3].every(function (otherPosition) {
        return otherPosition === position || selected[otherPosition] === undefined || variant['option' + otherPosition] === selected[otherPosition];
      });
    });
  }

  function updateSoldOutOptionValues(productSection, product) {
    var selected = getSelectedOptions(productSection);
    var soldOutLabel = (window.languages && window.languages.productFormSoldOut) || 'Sold out';

    productSection.querySelectorAll(OPTION_VALUES).forEach(function (element) {
      var position = parseInt(element.getAttribute('data-option-position'), 10);
      var value = getOptionValue(element);
      var isSoldOut = !isOptionValueAvailable(product, selected, position, value);

      element.classList.toggle('te-is-sold-out', isSoldOut);

      // Radios already have a hidden text label; list buttons need the state spelled out for screen readers
      if (element.tagName !== 'INPUT') {
        if (isSoldOut) {
          element.setAttribute('aria-label', value + ' – ' + soldOutLabel);
        } else {
          element.removeAttribute('aria-label');
        }
      }
    });

    // Standard dropdown style: say "Sold out" in the dropdown's own options
    productSection.querySelectorAll('[data-te-option-select]').forEach(function (select) {
      var radio = select.closest('.ProductForm__Option').querySelector('[type="radio"][data-option-position]');

      if (!radio) {
        return;
      }

      var position = parseInt(radio.getAttribute('data-option-position'), 10);

      Array.prototype.forEach.call(select.options, function (option) {
        var isSoldOut = !isOptionValueAvailable(product, selected, position, option.value);

        option.textContent = option.value + (isSoldOut ? ' – ' + soldOutLabel : '');
      });
    });
  }

  function initSoldOutOptionValues(productSection) {
    var productJson = productSection.querySelector('[data-product-json]');

    if (!productJson || productSection.hasAttribute('data-te-sold-out-options')) {
      return;
    }

    // Also for single-variant products: when that one variant is sold out, its colour/size is shown crossed out
    var product = JSON.parse(productJson.innerHTML).product;

    productSection.setAttribute('data-te-sold-out-options', '');
    updateSoldOutOptionValues(productSection, product);

    productSection.addEventListener('variant:changed', function () {
      updateSoldOutOptionValues(productSection, product);
    });
  }

  /* ---------------------------------------------------------------------------
     Wishlist (custom, saved in the shopper's browser)

     The list is kept in localStorage as [{ handle, id }], newest first. Hearts are buttons with
     [data-te-wishlist-toggle] (snippets/te-wishlist-button.liquid, and assets/te-product-card.js for the Boost grid).
     Clicks are handled for the whole page, and a MutationObserver syncs hearts added later (Boost grid,
     recommendations, recently viewed), so no card needs initialising. Every change fires "te:wishlist:changed".
     --------------------------------------------------------------------------- */

  var WISHLIST_STORAGE_KEY = 'te-wishlist';
  var WISHLIST_MAX_ITEMS = 100;
  var WISHLIST_LABEL_ADD = 'Add to wishlist';
  var WISHLIST_LABEL_REMOVE = 'Remove from wishlist';
  var WISHLIST_TEXT_SAVED = 'Saved to wishlist';

  var wishlistItems = readWishlist();

  function readWishlist() {
    try {
      var stored = JSON.parse(localStorage.getItem(WISHLIST_STORAGE_KEY) || '[]');

      return Array.isArray(stored) ? stored.filter(function (item) { return item && typeof item.handle === 'string'; }) : [];
    } catch (error) {
      return []; // Storage blocked (some private modes) or unreadable: start with an empty list
    }
  }

  function onWishlistChanged() {
    updateWishlistButtons();
    document.dispatchEvent(new CustomEvent('te:wishlist:changed', { detail: { items: wishlistItems.slice() } }));
  }

  function saveWishlist() {
    try {
      localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(wishlistItems));
    } catch (error) {
      // Storage blocked or full: the list still works until the shopper leaves the page
    }

    onWishlistChanged();
  }

  function isInWishlist(handle) {
    return wishlistItems.some(function (item) { return item.handle === handle; });
  }

  function addToWishlist(handle, id) {
    if (!handle || isInWishlist(handle)) {
      return;
    }

    wishlistItems.unshift({ handle: handle, id: id || null });
    wishlistItems = wishlistItems.slice(0, WISHLIST_MAX_ITEMS);
    saveWishlist();
  }

  function removeFromWishlist(handle) {
    wishlistItems = wishlistItems.filter(function (item) { return item.handle !== handle; });
    saveWishlist();
  }

  function updateWishlistButtons() {
    document.querySelectorAll('[data-te-wishlist-toggle]').forEach(function (button) {
      var isSaved = isInWishlist(button.getAttribute('data-product-handle'));
      var text = button.querySelector('[data-te-wishlist-text]');

      button.classList.toggle('is-active', isSaved);
      button.setAttribute('aria-pressed', isSaved ? 'true' : 'false');

      if (text) {
        text.textContent = isSaved ? WISHLIST_TEXT_SAVED : WISHLIST_LABEL_ADD;
      } else {
        button.setAttribute('aria-label', isSaved ? WISHLIST_LABEL_REMOVE : WISHLIST_LABEL_ADD);
      }
    });

    document.querySelectorAll('[data-te-wishlist-count]').forEach(function (count) {
      count.textContent = wishlistItems.length;
      count.classList.toggle('is-visible', wishlistItems.length > 0);
    });
  }

  // Meta Pixel standard event, so the Meta Ads plan can build audiences of shoppers who saved items
  function trackWishlistAdd(button) {
    if (typeof window.fbq !== 'function') {
      return;
    }

    window.fbq('track', 'AddToWishlist', {
      content_ids: [button.getAttribute('data-product-id')],
      content_name: button.getAttribute('data-product-title'),
      content_type: 'product',
      value: parseFloat(button.getAttribute('data-product-price')) || 0,
      currency: (window.theme && window.theme.shopCurrency) || 'AED'
    });
  }

  function toggleWishlist(button) {
    var handle = button.getAttribute('data-product-handle');

    if (isInWishlist(handle)) {
      removeFromWishlist(handle);
    } else {
      addToWishlist(handle, button.getAttribute('data-product-id'));
      trackWishlistAdd(button);
    }
  }

  function watchForNewWishlistButtons() {
    var isUpdateScheduled = false;

    new MutationObserver(function (mutations) {
      if (isUpdateScheduled) {
        return;
      }

      var hasNewButtons = mutations.some(function (mutation) {
        return Array.prototype.some.call(mutation.addedNodes, function (node) {
          return node.nodeType === 1 && (node.matches('[data-te-wishlist-toggle]') || node.querySelector('[data-te-wishlist-toggle]'));
        });
      });

      if (hasNewButtons) {
        isUpdateScheduled = true;

        requestAnimationFrame(function () {
          isUpdateScheduled = false;
          updateWishlistButtons();
        });
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ---------------------------------------------------------------------------
     Quick-add on product cards (snippets/te-card-actions.liquid, assets/te-product-card.js)

     Adds the variant through Shopify's cart API, then fires the theme's own "product:added" event so the cart
     drawer re-renders and opens, exactly as after the product page's Add to Cart.
     --------------------------------------------------------------------------- */

  function showQuickAddMessage(button, message) {
    var label = button.querySelector('.te-quick-add__label');
    var originalText = label.textContent;

    label.textContent = message;
    button.classList.add('te-quick-add--message');

    setTimeout(function () {
      label.textContent = originalText;
      button.classList.remove('te-quick-add--message');
    }, 2500);
  }

  function quickAdd(button) {
    if (button.getAttribute('aria-busy') === 'true') {
      return;
    }

    button.setAttribute('aria-busy', 'true');
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    // The cart drawer opens straight away in its "Adding…" state (assets/theme.js CartSection)
    button.dispatchEvent(new CustomEvent('product:adding', { bubbles: true }));

    fetch(((window.theme && window.theme.localeRootUrl) || '') + '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      // "sections" returns the refreshed cart drawer in the same answer (sections/te-cart-drawer.liquid)
      body: JSON.stringify({
        id: button.getAttribute('data-variant-id'),
        quantity: 1,
        sections: 'te-cart-drawer',
        sections_url: window.location.pathname
      })
    })
      .then(function (response) {
        return response.json().then(function (content) {
          if (!response.ok) {
            throw new Error(content.description || content.message || 'Could not add to cart');
          }

          // The cart page has no drawer: reload it to show the new item
          if (window.theme && window.theme.template === 'cart') {
            window.location.reload();
            return;
          }

          document.dispatchEvent(new CustomEvent('product:added', {
            bubbles: true,
            detail: { variant: content, quantity: 1, sections: content.sections }
          }));
        });
      })
      .catch(function (error) {
        // Closes the drawer again, so the message on the button can be seen
        button.dispatchEvent(new CustomEvent('product:add-failed', { bubbles: true }));
        showQuickAddMessage(button, error.message);
      })
      .then(function () {
        button.removeAttribute('aria-busy');
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      });
  }

  /* ---------------------------------------------------------------------------
     Wishlist page (sections/te-wishlist.liquid)

     Each saved product's card comes from sections/te-wishlist-card.liquid through the Section Rendering API, so it
     looks exactly like cards elsewhere on the site. Products that no longer exist are dropped from the wishlist.
     A shared link (?items=handle1,handle2) shows that list instead, with "Save all to my wishlist".
     --------------------------------------------------------------------------- */

  var WISHLIST_CARD_SECTION = 'te-wishlist-card';
  var WISHLIST_PAGE_REQUESTS = 4; // Cards loaded in parallel

  function fetchWishlistCard(handle) {
    var url = ((window.theme && window.theme.localeRootUrl) || '') + '/products/' + encodeURIComponent(handle) + '?section_id=' + WISHLIST_CARD_SECTION;

    return fetch(url, { credentials: 'same-origin' }).then(function (response) {
      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error('Could not load ' + handle);
      }

      return response.text().then(function (html) {
        var container = document.createElement('div');

        container.innerHTML = html;

        return container.querySelector('[data-te-wishlist-card]');
      });
    });
  }

  // Re-run the theme's currency conversion (it skips prices already converted) so new cards follow the picker
  function convertNewPrices() {
    var currencySelect = document.querySelector('.CurrencySelector__Select');

    if (window.theme && window.theme.currencyConversionEnabled && currencySelect) {
      currencySelect.dispatchEvent(new Event('change'));
    }
  }

  function updateWishlistPage(page, isSharedView) {
    var count = page.querySelectorAll('.te-wishlist__cell').length;
    var shareUrl = window.location.origin + window.location.pathname + '?items='
      + wishlistItems.map(function (item) { return encodeURIComponent(item.handle); }).join(',');
    var whatsappLink = page.querySelector('[data-te-wishlist-whatsapp]');

    page.querySelector('[data-te-wishlist-toolbar]').hidden = isSharedView || count === 0;
    page.querySelector('[data-te-wishlist-shared]').hidden = !isSharedView || count === 0;
    page.querySelector('[data-te-wishlist-empty]').hidden = count > 0;
    page.querySelector('[data-te-wishlist-summary]').textContent = count + (count === 1 ? ' item' : ' items');

    whatsappLink.href = 'https://wa.me/?text=' + encodeURIComponent(page.getAttribute('data-share-text') + ' ' + shareUrl);
    page.setAttribute('data-share-url', shareUrl);
  }

  function initWishlistPage(page) {
    if (page.hasAttribute('data-te-initialized')) {
      return;
    }

    page.setAttribute('data-te-initialized', '');

    var grid = page.querySelector('[data-te-wishlist-grid]');
    var copyButton = page.querySelector('[data-te-wishlist-copy]');
    var copyLabel = copyButton.textContent;
    var sharedParameter = new URLSearchParams(window.location.search).get('items');
    var isSharedView = !!sharedParameter;
    var handles = isSharedView ? sharedParameter.split(',') : wishlistItems.map(function (item) { return item.handle; });
    var cells = {};

    // Unique, non-empty handles, in wishlist order
    var trimmedHandles = handles.map(function (handle) { return handle.trim(); });

    handles = trimmedHandles.filter(function (handle, index) {
      return handle !== '' && trimmedHandles.indexOf(handle) === index;
    }).slice(0, WISHLIST_MAX_ITEMS);

    // One cell per product, filled when its card arrives
    handles.forEach(function (handle) {
      var cell = document.createElement('div');

      cell.className = 'Grid__Cell 1/2--phone 1/3--tablet-and-up 1/4--desk te-wishlist__cell is-loading';
      cell.setAttribute('data-product-handle', handle);
      grid.appendChild(cell);
      cells[handle] = cell;
    });

    updateWishlistPage(page, isSharedView);

    var queue = handles.slice();

    function loadNextCard() {
      var handle = queue.shift();

      if (handle === undefined) {
        return Promise.resolve();
      }

      var cell = cells[handle];

      return fetchWishlistCard(handle)
        .then(function (card) {
          if (!cell.isConnected) {
            return; // Removed from the wishlist while loading
          }

          if (card) {
            cell.appendChild(card);
            cell.classList.remove('is-loading');
          } else {
            cell.remove();

            if (!isSharedView) {
              removeFromWishlist(handle); // The product no longer exists
            }
          }
        }, function () {
          cell.remove(); // Network error: hide it for now but keep it in the wishlist
        })
        .then(loadNextCard);
    }

    var requests = [];

    for (var i = 0; i < WISHLIST_PAGE_REQUESTS; i++) {
      requests.push(loadNextCard());
    }

    Promise.all(requests).then(function () {
      updateWishlistPage(page, isSharedView);
      convertNewPrices();
    });

    // Take out the cards of products removed from the wishlist (on this page or in another tab)
    document.addEventListener('te:wishlist:changed', function () {
      if (!isSharedView) {
        grid.querySelectorAll('.te-wishlist__cell').forEach(function (cell) {
          if (!isInWishlist(cell.getAttribute('data-product-handle'))) {
            cell.remove();
          }
        });
      }

      updateWishlistPage(page, isSharedView);
    });

    copyButton.addEventListener('click', function () {
      var shareUrl = page.getAttribute('data-share-url');

      function showCopied() {
        copyButton.textContent = 'Link copied';

        setTimeout(function () {
          copyButton.textContent = copyLabel;
        }, 2000);
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(shareUrl).then(showCopied, function () {
          window.prompt('Copy this link:', shareUrl);
        });
      } else {
        window.prompt('Copy this link:', shareUrl);
      }
    });

    page.querySelector('[data-te-wishlist-save-all]').addEventListener('click', function () {
      // Added last to first, as each new item goes to the top: the saved list keeps the shared order
      Array.prototype.slice.call(grid.querySelectorAll('.te-wishlist__cell')).reverse().forEach(function (cell) {
        var button = cell.querySelector('[data-te-wishlist-toggle]');

        addToWishlist(cell.getAttribute('data-product-handle'), button ? button.getAttribute('data-product-id') : null);
      });

      window.location.href = window.location.pathname;
    });
  }

  /* ---------------------------------------------------------------------------
     Quick view (snippets/te-quickview-modal.liquid, sections/te-quickview.liquid)

     Opens a product in the theme's own Modal. The product comes from sections/te-quickview.liquid through the Section
     Rendering API and uses the "featured-product" section type, so on "shopify:section:load" the theme itself runs the
     variant picker and the Ajax add to cart (which fires "product:added" and opens the cart drawer). The section is
     unloaded again when the popup closes or shows another product.
     --------------------------------------------------------------------------- */

  var QUICKVIEW_SECTION = 'te-quickview';
  var quickViewCache = {};
  var quickViewRequest = 0; // Increases on every open and close, so a late answer for an older request is ignored
  var quickViewTrigger = null;
  var isQuickViewOpen = false;
  var isQuickViewSectionLoaded = false;

  function getQuickViewModal() {
    return document.querySelector('[data-te-quickview-modal]');
  }

  // Cached per product as a promise, so a click while a prefetch (hover, touch, focus) is still loading reuses that
  // request. Parsing the answer already starts downloading the first photo (a plain img, see te-quickview-slide), so it
  // is usually in the browser's cache by the time the popup shows it.
  function fetchQuickView(handle) {
    if (quickViewCache[handle]) {
      return quickViewCache[handle];
    }

    var url = ((window.theme && window.theme.localeRootUrl) || '') + '/products/' + encodeURIComponent(handle) + '?section_id=' + QUICKVIEW_SECTION;

    quickViewCache[handle] = fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Could not load ' + handle);
        }

        return response.text();
      })
      .then(function (html) {
        var container = document.createElement('div');

        container.innerHTML = html;

        var product = container.querySelector('[data-section-type="featured-product"]');

        if (!product) {
          throw new Error('Product not found: ' + handle);
        }

        return product.outerHTML;
      });

    // A failed request is tried again on the next open
    quickViewCache[handle].catch(function () {
      delete quickViewCache[handle];
    });

    return quickViewCache[handle];
  }

  // Loads a quick view ahead of the click; failures stay silent (the click shows the popup's error message)
  function prefetchQuickView(trigger) {
    var handle = trigger && trigger.getAttribute('data-product-handle');

    if (handle && getQuickViewModal()) {
      fetchQuickView(handle).catch(function () {});
    }
  }

  // Stop the theme's product code for the previous quick view (SectionContainer in assets/theme.js)
  function unloadQuickViewSection() {
    if (isQuickViewSectionLoaded) {
      isQuickViewSectionLoaded = false;
      document.dispatchEvent(new CustomEvent('shopify:section:unload', { detail: { sectionId: QUICKVIEW_SECTION } }));
    }
  }

  // Meta Pixel standard event, like AddToWishlist: counts quick views as product views for the Meta Ads plan
  function trackQuickViewContent(productElement) {
    var productJson = productElement.querySelector('[data-product-json]');

    if (typeof window.fbq !== 'function' || !productJson) {
      return;
    }

    var product = JSON.parse(productJson.innerHTML).product;

    window.fbq('track', 'ViewContent', {
      content_ids: [String(product.id)],
      content_name: product.title,
      content_type: 'product',
      value: product.price / 100,
      currency: (window.theme && window.theme.shopCurrency) || 'AED'
    });
  }

  function initQuickViewGallery(productElement) {
    var slides = productElement.querySelector('[data-te-quickview-slides]');

    if (!slides) {
      return;
    }

    productElement.querySelectorAll('[data-te-quickview-arrow]').forEach(function (arrow) {
      arrow.addEventListener('click', function () {
        var direction = arrow.getAttribute('data-te-quickview-arrow') === 'next' ? 1 : -1;

        slides.scrollBy({ left: direction * slides.clientWidth, behavior: 'smooth' });
      });
    });

    // The theme puts the chosen variant's image in the first slide: bring it into view
    productElement.addEventListener('variant:changed', function () {
      slides.scrollTo({ left: 0, behavior: 'smooth' });
    });
  }

  // Returns false when there is no popup on the page, so a "Choose size" link can simply be followed
  function openQuickView(trigger) {
    var modal = getQuickViewModal();
    var handle = trigger.getAttribute('data-product-handle');

    if (!modal || !handle) {
      return false;
    }

    var content = modal.querySelector('[data-te-quickview-content]');
    var request = ++quickViewRequest;
    var productUrl = trigger.getAttribute('href') || ((window.theme && window.theme.localeRootUrl) || '') + '/products/' + encodeURIComponent(handle);

    unloadQuickViewSection();
    quickViewTrigger = trigger;
    content.innerHTML = '';
    modal.setAttribute('data-state', 'loading');
    modal.querySelector('[data-te-quickview-error-link]').setAttribute('href', productUrl);

    if (!isQuickViewOpen) {
      isQuickViewOpen = true;
      document.documentElement.classList.add('te-quickview-open');
      document.querySelector('[data-te-quickview-opener]').click();
    }

    fetchQuickView(handle)
      .then(function (html) {
        if (request !== quickViewRequest) {
          return; // The popup was closed, or another product was opened meanwhile
        }

        content.innerHTML = html;
        modal.setAttribute('data-state', 'ready');

        // The theme creates its product code (variant picker, add to cart) for the new markup
        content.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId: QUICKVIEW_SECTION } }));
        isQuickViewSectionLoaded = true;

        initQuickViewGallery(content.firstElementChild);
        convertNewPrices();
        trackQuickViewContent(content.firstElementChild);
      })
      .catch(function () {
        if (request === quickViewRequest) {
          modal.setAttribute('data-state', 'error');
        }
      });

    return true;
  }

  function onQuickViewClosed() {
    isQuickViewOpen = false;
    quickViewRequest++; // Ignore a product that is still loading
    unloadQuickViewSection();
    document.documentElement.classList.remove('te-quickview-open');

    if (quickViewTrigger && quickViewTrigger.isConnected) {
      quickViewTrigger.focus();
    }

    quickViewTrigger = null;
  }

  function watchQuickViewModal() {
    var modal = getQuickViewModal();

    if (!modal) {
      return;
    }

    // The theme's Modal code closes the popup (close button, overlay, Escape) by setting aria-hidden
    new MutationObserver(function () {
      if (isQuickViewOpen && modal.getAttribute('aria-hidden') === 'true') {
        onQuickViewClosed();
      }
    }).observe(modal, { attributes: true, attributeFilter: ['aria-hidden'] });

    // Adding from the popup keeps the popup open (its button shows the wait, and an error shows inside it), so the cart
    // drawer must not open over it at the tap: stop the theme's "product:adding" before it reaches the drawer.
    ['product:adding', 'product:add-failed'].forEach(function (eventName) {
      modal.addEventListener(eventName, function (event) {
        event.stopPropagation();
      });
    });

    // Once the product is added, close the popup so the cart drawer that opens can be seen
    document.addEventListener('product:added', function () {
      if (!isQuickViewOpen) {
        return;
      }

      // The cart page has no drawer: reload it to show the new item
      if (window.theme && window.theme.template === 'cart') {
        window.location.reload();
        return;
      }

      modal.querySelector('[data-action="close-modal"]').click();
    });
  }

  /* ---------------------------------------------------------------------------
     Video reels (sections/te-video-reels.liquid)
     --------------------------------------------------------------------------- */

  var REEL_LABEL_PLAY = 'Play video';
  var REEL_LABEL_PAUSE = 'Pause video';
  var REEL_LABEL_MUTE = 'Mute video';
  var REEL_LABEL_UNMUTE = 'Unmute video';
  var REEL_LABEL_DOT = 'Go to slide ';
  var REEL_PLAY_RATIO = 0.7; // Share of a reel on screen before it plays by itself (a reel peeking in on phones doesn't)
  var REEL_STOP_RATIO = 0.25; // Below this it stops, so scrolling a little doesn't cut off a reel being watched

  function updateReelCard(video) {
    var card = video.closest('[data-te-reel]');
    var isPlaying = !video.paused;

    card.classList.toggle('is-playing', isPlaying);
    card.classList.toggle('is-paused', !isPlaying);
    card.classList.toggle('is-muted', video.muted);
    card.querySelector('[data-te-reel-toggle]').setAttribute('aria-label', isPlaying ? REEL_LABEL_PAUSE : REEL_LABEL_PLAY);
    card.querySelector('[data-te-reel-mute]').setAttribute('aria-label', video.muted ? REEL_LABEL_UNMUTE : REEL_LABEL_MUTE);
  }

  function playReel(video, withSound) {
    if (withSound) {
      // Only one reel has sound: the others keep playing silently
      document.querySelectorAll('[data-te-reel-video]').forEach(function (otherVideo) {
        if (otherVideo !== video) {
          otherVideo.muted = true;
        }
      });
    }

    video.muted = !withSound;

    var playing = video.play();

    // Blocked (e.g. iOS Low Power Mode) or cut short by a pause: show the real state, with the Play button
    if (playing && playing.catch) {
      playing.catch(function () {
        updateReelCard(video);
      });
    }
  }

  // Centre button or the video itself: pause, or play with sound
  function toggleReel(video) {
    if (video.paused) {
      video.tePausedByShopper = false;
      playReel(video, true);
    } else {
      video.tePausedByShopper = true;
      video.pause();
    }
  }

  // Speaker button: sound on (playing the reel if it was paused) or off
  function toggleReelSound(video) {
    if (video.muted) {
      video.tePausedByShopper = false;
      playReel(video, true);
    } else {
      video.muted = true;
    }
  }

  // Reels play silently while on screen and stop (and go quiet again) when scrolled or slid away. A reel the shopper
  // paused stays paused.
  function watchReelVisibility(section) {
    var canAutoplay = section.getAttribute('data-autoplay') === 'true'
      && !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
      && !(navigator.connection && navigator.connection.saveData);

    if (!('IntersectionObserver' in window)) {
      return null;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        var ratio = entry.isIntersecting ? entry.intersectionRatio : 0;

        if (ratio >= REEL_PLAY_RATIO) {
          if (canAutoplay && video.paused && !video.tePausedByShopper) {
            playReel(video, false);
          }
        } else if (ratio < REEL_STOP_RATIO) {
          video.pause();
          video.muted = true;
        }
      });
    }, { threshold: [0, REEL_STOP_RATIO, REEL_PLAY_RATIO] });

    section.querySelectorAll('[data-te-reel-video]').forEach(function (video) {
      observer.observe(video);
    });

    return observer;
  }

  function renderReelsDots(reels) {
    var count = reels.lastIndex > 0 ? reels.lastIndex + 1 : 0;
    var html = '';

    if (reels.dots.children.length === count) {
      return;
    }

    for (var index = 0; index < count; index++) {
      html += '<button type="button" class="te-video-reels__dot" data-te-video-reels-dot="' + index + '" aria-label="' + REEL_LABEL_DOT + (index + 1) + '"></button>';
    }

    reels.dots.innerHTML = html;
  }

  function updateReelsNav(reels) {
    var index = reels.flickity.selectedIndex;

    reels.previousButton.disabled = index === 0;
    reels.nextButton.disabled = index >= reels.lastIndex;

    Array.prototype.forEach.call(reels.dots.children, function (dot, dotIndex) {
      dot.classList.toggle('is-selected', dotIndex === index);
      dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
    });
  }

  // Works out how many reels fit on screen (CSS sets the widths per screen size). Each dot is one step of the row, and
  // the slider stops once the last reel is fully on screen.
  function updateReelsLayout(reels) {
    var flickity = reels.flickity;
    var cells = flickity.getCellElements();
    var viewportWidth = flickity.viewport.getBoundingClientRect().width;
    var cellWidth = cells[0].getBoundingClientRect().width;
    var fitsOnScreen;

    // Hidden (nothing to measure): Flickity's next resize runs this again
    if (!cellWidth || !viewportWidth) {
      return;
    }

    reels.visibleCount = Math.max(1, Math.floor(viewportWidth / cellWidth + 0.05)); // 0.05 absorbs rounding
    reels.lastIndex = Math.max(0, cells.length - reels.visibleCount);
    fitsOnScreen = reels.lastIndex === 0;

    // Every reel fits: centre the row and switch the slider controls off
    reels.section.classList.toggle('te-video-reels--static', fitsOnScreen);
    flickity.slider.style.marginLeft = fitsOnScreen ? Math.max(0, (viewportWidth - cells.length * cellWidth) / 2) + 'px' : '';

    if (fitsOnScreen) {
      flickity.unbindDrag();
    } else {
      flickity.bindDrag();
    }

    renderReelsDots(reels);

    if (flickity.selectedIndex > reels.lastIndex) {
      flickity.select(reels.lastIndex, false, true);
    } else {
      updateReelsNav(reels);
    }
  }

  // Flickity (loaded globally by libs.js) handles dragging and swiping. Its own arrows and dots are off: with
  // "contain" its last few dots don't move the row, so these controls stop at the last full row instead.
  function initReelsSlider(reels) {
    var flickity = reels.flickity = new window.Flickity(reels.track, {
      cellAlign: 'left',
      contain: true,
      pageDots: false,
      prevNextButtons: false,
      dragThreshold: 8
    });

    reels.section.classList.add('te-video-reels--slider');

    flickity.on('select', function () {
      // Past the last full row (dragged to the end, keyboard arrows): the row is already there, just sync the index
      if (flickity.selectedIndex > reels.lastIndex) {
        flickity.select(reels.lastIndex);
        return;
      }

      updateReelsNav(reels);
    });

    flickity.on('resize', function () {
      updateReelsLayout(reels);
    });

    reels.previousButton.addEventListener('click', function () {
      flickity.select(Math.max(0, flickity.selectedIndex - 1));
    });

    reels.nextButton.addEventListener('click', function () {
      flickity.select(Math.min(reels.lastIndex, flickity.selectedIndex + 1));
    });

    reels.dots.addEventListener('click', function (event) {
      var dot = event.target.closest('[data-te-video-reels-dot]');

      if (dot) {
        flickity.select(parseInt(dot.getAttribute('data-te-video-reels-dot'), 10));
      }
    });

    // Tabbing to a button in a reel that's off screen: slide it into view
    reels.track.addEventListener('focusin', function (event) {
      var cell = event.target.closest('.te-video-reels__cell');
      var index = cell ? flickity.getCellElements().indexOf(cell) : -1;

      if (index !== -1 && (index < flickity.selectedIndex || index >= flickity.selectedIndex + reels.visibleCount)) {
        flickity.select(Math.min(index, reels.lastIndex));
      }
    });

    // Focusing a hidden button makes the browser scroll the clipped viewport, which would throw Flickity's positions off
    flickity.viewport.addEventListener('scroll', function () {
      flickity.viewport.scrollLeft = 0;
    });

    updateReelsLayout(reels);
  }

  function initVideoReels(section) {
    var track = section.querySelector('[data-te-video-reels-track]');

    if (section.teVideoReels || !track) {
      return;
    }

    var reels = section.teVideoReels = {
      section: section,
      track: track,
      dots: section.querySelector('[data-te-video-reels-dots]'),
      previousButton: section.querySelector('[data-te-video-reels-prev]'),
      nextButton: section.querySelector('[data-te-video-reels-next]'),
      flickity: null,
      observer: null,
      visibleCount: 1,
      lastIndex: 0
    };

    section.querySelectorAll('[data-te-reel-video]').forEach(function (video) {
      video.muted = true;

      ['play', 'pause', 'volumechange'].forEach(function (type) {
        video.addEventListener(type, function () {
          updateReelCard(video);
        });
      });

      updateReelCard(video);
    });

    if (typeof window.Flickity === 'function') {
      initReelsSlider(reels);
    }

    reels.observer = watchReelVisibility(section);
  }

  function destroyVideoReels(section) {
    var reels = section.teVideoReels;

    if (!reels) {
      return;
    }

    if (reels.observer) {
      reels.observer.disconnect();
    }

    // Stops its resize listener (this theme's copy of Flickity leaves the markup as it is)
    if (reels.flickity) {
      reels.flickity.destroy();
    }

    section.querySelectorAll('[data-te-reel-video]').forEach(function (video) {
      video.pause();
    });

    delete section.teVideoReels;
  }

  /* ---------------------------------------------------------------------------
     Marquee rows: Instagram marquee (sections/te-instagram-marquee.liquid), press logos (sections/logo-list.liquid)
     and the announcement bar (sections/announcement.liquid)

     All rows are printed twice and slide by one copy in CSS. data-te-marquee holds the class prefix of the row's
     parts (te-ig__viewport, te-press__track...).
     --------------------------------------------------------------------------- */

  function showLoadedImage(image) {
    image.classList.add('is-loaded');
  }

  // onLoad (showLoadedImage by default) may run twice for a cached image; it must not mind
  function loadMarqueeImage(image, onLoad) {
    var handleLoad = onLoad || showLoadedImage;

    image.addEventListener('load', handleLoad.bind(null, image));

    if (image.hasAttribute('data-srcset')) {
      image.setAttribute('srcset', image.getAttribute('data-srcset'));
      image.removeAttribute('data-srcset');
    }

    image.setAttribute('src', image.getAttribute('data-src'));
    image.removeAttribute('data-src');

    // Already in the browser's cache (the second copy of each image)
    if (image.complete && image.naturalWidth > 0) {
      handleLoad(image);
    }
  }

  // Every image is fetched as the section comes near, not one by one: lazy loading only starts an image once it is
  // inside the row's visible area, so images sliding in from the edge would appear blank on the first loop
  function loadMarqueeImages(section, selector, onLoad) {
    section.querySelectorAll(selector).forEach(function (image) {
      loadMarqueeImage(image, onLoad);
    });
  }

  function watchMarquee(section, imageSelector, onLoad) {
    if (!('IntersectionObserver' in window)) {
      loadMarqueeImages(section, imageSelector, onLoad);
      return;
    }

    // A callback can bring several queued entries for the section (e.g. after a fast scroll): any of them counts for
    // loading, but only the latest says whether it is on screen now
    var loader = new IntersectionObserver(function (entries) {
      var isNear = entries.some(function (entry) { return entry.isIntersecting; });

      if (isNear) {
        loader.disconnect();
        loadMarqueeImages(section, imageSelector, onLoad);
      }
    }, { rootMargin: '600px 0px' });

    loader.observe(section);

    // The row only moves while it is on screen
    new IntersectionObserver(function (entries) {
      section.classList.toggle('is-offscreen', !entries[entries.length - 1].isIntersecting);
    }).observe(section);
  }

  function findMarquee(sectionId) {
    var wrapper = document.getElementById('shopify-section-' + sectionId);

    return wrapper && wrapper.querySelector('[data-te-marquee]');
  }

  // Theme editor: the row stops with the item picked in the sidebar centred (or as near as it can get)
  function holdMarqueeAt(section, item) {
    var prefix = section.getAttribute('data-te-marquee');
    var viewport = section.querySelector('.' + prefix + '__viewport');
    var track = section.querySelector('.' + prefix + '__track');
    var itemLeft = item.getBoundingClientRect().left - track.getBoundingClientRect().left;
    var offset = Math.min(0, (viewport.clientWidth - item.offsetWidth) / 2 - itemLeft);

    section.style.setProperty('--' + prefix + '-offset', offset + 'px');
    section.classList.add('is-editing');
  }

  /* ---------------------------------------------------------------------------
     Instagram marquee (sections/te-instagram-marquee.liquid)

     With dozens of photos, loading them all as the row comes near cost ~0.8 MB on desktop and ~2 MB on phones, for a
     row that shows 7 (or 2-3) at a time. Each photo now loads, with its blurred preview, when it gets within reach of
     the visible row: on the side it slides in from, half a screen (at least two photos) ahead, so it is there before
     it shows. Both copies of a photo share one file, so they load together for one download. The reduced-motion row
     is swiped by hand, so there it looks ahead on both sides. Checked every half second while the row is near.
     --------------------------------------------------------------------------- */

  var IG_REACH_SCREENS = 0.5;
  var IG_REACH_MIN_PHOTOS = 2;
  var IG_CHECK_INTERVAL = 500;
  var igPrefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function loadInstagramPhoto(ig, source) {
    ig.photos[source].forEach(function (image) {
      var media = image.closest('.te-ig__media');
      var preview = media && media.getAttribute('data-te-ig-preview');

      if (preview) {
        media.style.backgroundImage = 'url("' + preview + '")';
        media.removeAttribute('data-te-ig-preview');
      }

      loadMarqueeImage(image);
    });

    delete ig.photos[source];
  }

  function loadInstagramPhotosInReach(ig) {
    var sources = Object.keys(ig.photos);

    if (!sources.length) {
      stopInstagramChecks(ig);
      return;
    }

    var box = ig.viewport.getBoundingClientRect();
    var item = ig.viewport.querySelector('.te-ig__item');
    var reach = Math.max(box.width * IG_REACH_SCREENS, (item ? item.offsetWidth : 0) * IG_REACH_MIN_PHOTOS);
    var fromLeft = ig.section.classList.contains('te-ig--right');
    var bothSides = igPrefersReducedMotion.matches;
    var left = box.left - (fromLeft || bothSides ? reach : 0);
    var right = box.right + (!fromLeft || bothSides ? reach : 0);

    sources.forEach(function (source) {
      var inReach = ig.photos[source].some(function (image) {
        var rect = image.closest('.te-ig__item').getBoundingClientRect();

        return rect.width > 0 && rect.right > left && rect.left < right;
      });

      if (inReach) {
        loadInstagramPhoto(ig, source);
      }
    });
  }

  function startInstagramChecks(ig) {
    loadInstagramPhotosInReach(ig);

    if (!ig.timer && Object.keys(ig.photos).length) {
      ig.timer = setInterval(loadInstagramPhotosInReach.bind(null, ig), IG_CHECK_INTERVAL);
    }
  }

  function stopInstagramChecks(ig) {
    clearInterval(ig.timer);
    ig.timer = null;
  }

  // The section prints one copy of the row; the loop slides by one copy's width, so a second one follows it. The
  // copy is for the eye only: hidden from screen readers and the keyboard, without the editor's block attributes.
  function addInstagramCopy(section) {
    var track = section.querySelector('.te-ig__track');
    var groups = track ? track.querySelectorAll('.te-ig__group') : [];

    if (groups.length !== 1) {
      return;
    }

    var copy = groups[0].cloneNode(true);

    copy.setAttribute('aria-hidden', 'true');

    copy.querySelectorAll('.te-ig__item').forEach(function (item) {
      Array.prototype.slice.call(item.attributes).forEach(function (attribute) {
        if (attribute.name === 'data-block-id' || attribute.name.indexOf('data-shopify') === 0) {
          item.removeAttribute(attribute.name);
        }
      });

      item.classList.add('te-ig__item--clone');
      item.setAttribute('aria-hidden', 'true');
    });

    copy.querySelectorAll('a').forEach(function (link) {
      link.setAttribute('tabindex', '-1');
    });

    copy.querySelectorAll('noscript, .u-visually-hidden').forEach(function (element) {
      element.parentNode.removeChild(element);
    });

    track.appendChild(copy);
  }

  function initInstagramMarquee(section) {
    if (section.teInstagramMarquee) {
      return;
    }

    addInstagramCopy(section);

    var viewport = section.querySelector('.te-ig__viewport');

    if (!viewport || !('IntersectionObserver' in window)) {
      section.teInstagramMarquee = {};
      section.querySelectorAll('[data-te-ig-preview]').forEach(function (media) {
        media.style.backgroundImage = 'url("' + media.getAttribute('data-te-ig-preview') + '")';
      });
      watchMarquee(section, '.te-ig__image[data-src]');
      return;
    }

    var ig = section.teInstagramMarquee = { section: section, viewport: viewport, photos: {}, timer: null };

    section.querySelectorAll('.te-ig__image[data-src]').forEach(function (image) {
      var source = image.getAttribute('data-src');

      (ig.photos[source] = ig.photos[source] || []).push(image);
    });

    ig.loader = new IntersectionObserver(function (entries) {
      if (entries[entries.length - 1].isIntersecting) {
        startInstagramChecks(ig);
      } else {
        stopInstagramChecks(ig);
      }
    }, { rootMargin: '600px 0px' });

    ig.loader.observe(section);

    // The row only moves while it is on screen
    ig.visibility = new IntersectionObserver(function (entries) {
      section.classList.toggle('is-offscreen', !entries[entries.length - 1].isIntersecting);
    });

    ig.visibility.observe(section);

    // The reduced-motion row is swiped by hand, and the theme editor slides the row to a picked photo
    ig.onScroll = loadInstagramPhotosInReach.bind(null, ig);
    viewport.addEventListener('scroll', ig.onScroll, { passive: true });
  }

  function destroyInstagramMarquee(section) {
    var ig = section.teInstagramMarquee;

    if (ig && ig.loader) {
      stopInstagramChecks(ig);
      ig.loader.disconnect();
      ig.visibility.disconnect();
      ig.viewport.removeEventListener('scroll', ig.onScroll);
    }

    delete section.teInstagramMarquee;
  }

  /* ---------------------------------------------------------------------------
     Press logos (sections/logo-list.liquid)

     Logo files come with very different white margins (some fill the file, others sit small in the middle), so each
     logo is measured once it loads: the box around its dark pixels becomes its crop and its shape, and the CSS sizes
     every logo from that shape. The logos arrive with crossorigin="anonymous" so the canvas may read them; if it
     can't, the logo keeps its whole file.
     --------------------------------------------------------------------------- */

  var PRESS_MEASURE_WIDTH = 400;
  var pressLogoCrops = {};

  function isLogoInk(pixels, index) {
    // Visible, and darker than the white background JPG logos come on
    return pixels[index + 3] > 32 && (pixels[index] < 225 || pixels[index + 1] < 225 || pixels[index + 2] < 225);
  }

  // The box around the logo's dark pixels, as fractions of the image, or null (canvas can't be read, or no dark
  // pixels at all, e.g. a white logo)
  function measurePressLogo(image) {
    var scale = Math.min(1, PRESS_MEASURE_WIDTH / image.naturalWidth);
    var width = Math.max(1, Math.round(image.naturalWidth * scale));
    var height = Math.max(1, Math.round(image.naturalHeight * scale));
    var canvas = document.createElement('canvas');
    var pixels;

    canvas.width = width;
    canvas.height = height;

    try {
      var context = canvas.getContext('2d');

      context.drawImage(image, 0, 0, width, height);
      pixels = context.getImageData(0, 0, width, height).data;
    } catch (error) {
      return null;
    }

    var left = width;
    var top = height;
    var right = -1;
    var bottom = -1;

    for (var y = 0; y < height; y++) {
      for (var x = 0; x < width; x++) {
        if (isLogoInk(pixels, (y * width + x) * 4)) {
          left = Math.min(left, x);
          right = Math.max(right, x);
          top = Math.min(top, y);
          bottom = Math.max(bottom, y);
        }
      }
    }

    if (right < 0) {
      return null;
    }

    // One pixel of room so soft edges are not cut
    left = Math.max(0, left - 1);
    top = Math.max(0, top - 1);
    right = Math.min(width - 1, right + 1);
    bottom = Math.min(height - 1, bottom + 1);

    return {
      x: left / width,
      y: top / height,
      width: (right - left + 1) / width,
      height: (bottom - top + 1) / height
    };
  }

  // Crops the logo to its ink before it fades in; every copy of a logo reuses the first measurement
  function showPressLogo(image) {
    var source = image.currentSrc || image.src;
    var item = image.closest('.te-press__item');

    if (!pressLogoCrops.hasOwnProperty(source)) {
      pressLogoCrops[source] = measurePressLogo(image);
    }

    var crop = pressLogoCrops[source];

    if (crop && item) {
      item.style.setProperty('--te-press-ar', ((crop.width * image.naturalWidth) / (crop.height * image.naturalHeight)).toFixed(4));
      item.style.setProperty('--te-press-crop-x', crop.x.toFixed(4));
      item.style.setProperty('--te-press-crop-y', crop.y.toFixed(4));
      item.style.setProperty('--te-press-crop-w', crop.width.toFixed(4));
      item.style.setProperty('--te-press-crop-h', crop.height.toFixed(4));
    }

    showLoadedImage(image);
  }

  function initPressLogos(section) {
    if (section.tePressLogos) {
      return;
    }

    section.tePressLogos = true;
    watchMarquee(section, '.te-press__image[data-src]', showPressLogo);
  }

  /* ---------------------------------------------------------------------------
     Announcement bar marquee (sections/announcement.liquid)
     --------------------------------------------------------------------------- */

  // Liquid guesses the duration from the letter count; this sets it from the real width of one copy so the text moves
  // at the chosen speed. It runs again once the web font is in, while the row has barely moved, so the change is not seen.
  function setAnnouncementDuration(section) {
    var group = section.querySelector('.te-announce__group');
    var speed = parseFloat(section.getAttribute('data-te-announce-speed'));
    var width = group ? group.getBoundingClientRect().width : 0;

    if (width > 0 && speed > 0) {
      section.style.setProperty('--te-announce-duration', (width / speed).toFixed(2) + 's');
    }
  }

  function setAnnouncementBarHeight(height) {
    document.documentElement.style.setProperty('--announcement-bar-height', height + 'px');
  }

  // Nothing is remembered: the bar is back on the next page or refresh. It slides shut, then the header offsets that
  // count the bar (--announcement-bar-height) drop to 0. Keyboard focus moves on to the header.
  function closeAnnouncementBar(section) {
    var hadFocus = section.contains(document.activeElement);
    var headerControls = document.querySelectorAll('#shopify-section-header .Header__Wrapper a[href], #shopify-section-header .Header__Wrapper button');

    function finish() {
      section.classList.remove('is-closing');
      section.classList.add('is-closed');
      section.style.height = '';
      setAnnouncementBarHeight(0);
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
    } else {
      section.style.height = section.offsetHeight + 'px';
      section.getBoundingClientRect(); // applies the height before it animates to 0
      section.classList.add('is-closing');

      section.addEventListener('transitionend', function onEnd(event) {
        if (event.target === section && event.propertyName === 'height') {
          section.removeEventListener('transitionend', onEnd);
          finish();
        }
      });
    }

    // The first control that takes focus: the ones hidden at this screen size refuse it
    if (hadFocus) {
      Array.prototype.some.call(headerControls, function (control) {
        control.focus();
        return document.activeElement === control;
      });
    }
  }

  function initAnnouncementBar(section) {
    if (section.teAnnouncementBar) {
      return;
    }

    var closeButton = section.querySelector('[data-te-announce-close]');

    section.teAnnouncementBar = true;
    setAnnouncementDuration(section);
    watchMarquee(section, 'img[data-src]');

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(setAnnouncementDuration.bind(null, section));
    }

    if (closeButton) {
      closeButton.addEventListener('click', closeAnnouncementBar.bind(null, section));
    }
  }

  /* ---------------------------------------------------------------------------
     Newsletter signup (sections/te-newsletter.liquid)

     Sends Shopify's customer form in the background and shows the result in place. Shopify answers with the page it
     redirects to: "customer_posted=true" in its address means the email was saved, otherwise that page's copy of this
     form carries Shopify's error. When Shopify wants a captcha first (/challenge), the form is sent the normal way so
     the shopper can solve it, and Shopify brings them back to the thank-you message.
     --------------------------------------------------------------------------- */

  var NEWSLETTER_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function setNewsletterMessage(form, text) {
    var message = form.querySelector('[data-te-newsletter-message]');
    var input = form.querySelector('[name="contact[email]"]');

    if (message) {
      message.textContent = text;
    }

    if (input && text) {
      input.setAttribute('aria-invalid', 'true');
    } else if (input) {
      input.removeAttribute('aria-invalid');
    }
  }

  function setNewsletterLoading(form, isLoading) {
    var button = form.querySelector('[type="submit"]');

    form.classList.toggle('is-loading', isLoading);

    if (button) {
      button.disabled = isLoading;
    }
  }

  function showNewsletterSuccess(section, form) {
    var template = section.querySelector('[data-te-newsletter-success-template]');

    form.innerHTML = template.innerHTML;

    var success = form.querySelector('[data-te-newsletter-success]');

    if (success) {
      success.focus();
    }

    // The newsletter popup (assets/te-popup.js) stops asking anyone who has signed up, in any of the forms
    section.dispatchEvent(new CustomEvent('te:newsletter:subscribed', { bubbles: true }));

    // Meta Pixel standard event, so ads can be measured against signups
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead', { content_name: 'Newsletter signup' });
    }
  }

  // Shopify's own form script (captcha) cancels every customer form submit and posts the form itself, reloading the
  // page. data-nocaptcha keeps it away while the form is sent in the background; for the normal submit it is removed
  // again so Shopify can save the details it needs for the captcha page.
  function submitNewsletterNatively(form) {
    form.removeAttribute('data-nocaptcha');
    form.teSubmitNatively = true;

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      HTMLFormElement.prototype.submit.call(form);
    }
  }

  function readNewsletterReply(form, response, html) {
    if (/[?&]customer_posted=true/.test(response.url)) {
      return { success: true };
    }

    var reply = new DOMParser().parseFromString(html, 'text/html').getElementById(form.id);
    var message = reply && reply.querySelector('[data-te-newsletter-message]');

    if (reply && reply.querySelector('[data-te-newsletter-success]')) {
      return { success: true };
    }

    return { error: message ? message.textContent.trim() : '' };
  }

  function submitNewsletter(section, form) {
    var input = form.querySelector('[name="contact[email]"]');

    input.value = input.value.trim();

    if (!NEWSLETTER_EMAIL_PATTERN.test(input.value) || !input.checkValidity()) {
      setNewsletterMessage(form, section.getAttribute('data-invalid-message'));
      input.focus();
      return;
    }

    setNewsletterMessage(form, '');
    setNewsletterLoading(form, true);

    fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin' })
      .then(function (response) {
        // A human check: Shopify's captcha page, or Cloudflare's (403 with cf-mitigated), which only the full page can
        // show. Stays in the loading state while the page changes.
        if (/\/challenge/.test(response.url) || response.headers.get('cf-mitigated') === 'challenge') {
          submitNewsletterNatively(form);
          return;
        }

        return response.text().then(function (html) {
          var reply = readNewsletterReply(form, response, html);

          setNewsletterLoading(form, false);

          if (reply.success) {
            showNewsletterSuccess(section, form);
          } else {
            setNewsletterMessage(form, reply.error || section.getAttribute('data-error-message'));
            input.focus();
          }
        });
      })
      .catch(function () {
        setNewsletterLoading(form, false);
        setNewsletterMessage(form, section.getAttribute('data-error-message'));
      });
  }

  function copyNewsletterCode(button) {
    var box = button.closest('.te-newsletter__code, .te-popup__code');
    var code = box.querySelector('[data-te-newsletter-code]');
    var status = box.querySelector('[data-te-newsletter-copy-status]');

    var copied = function () {
      button.textContent = button.getAttribute('data-copied-label');
      button.classList.add('is-copied');

      if (status) {
        status.textContent = button.getAttribute('data-copied-label');
      }

      clearTimeout(button.teCopyTimer);

      button.teCopyTimer = setTimeout(function () {
        button.textContent = button.getAttribute('data-copy-label');
        button.classList.remove('is-copied');

        if (status) {
          status.textContent = '';
        }
      }, 2000);
    };

    // Older browsers: copy the selected code. If even that fails, the code stays selected to copy by hand.
    var copyBySelecting = function () {
      var range = document.createRange();
      var selection = window.getSelection();

      range.selectNodeContents(code);
      selection.removeAllRanges();
      selection.addRange(range);

      try {
        if (document.execCommand('copy')) {
          copied();
        }
      } catch (error) {
        // Left selected
      }
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(code.textContent.trim()).then(copied, copyBySelecting);
    } else {
      copyBySelecting();
    }
  }

  function initNewsletter(section) {
    // The Newsletter signup section's form, the footer's (sections/footer.liquid) or the popup's (sections/popup.liquid)
    var form = section.querySelector('.te-newsletter__form, .te-footer__form, .te-popup__form');

    // Without fetch the form simply posts and reloads
    if (section.teNewsletter || !form || typeof window.fetch !== 'function') {
      return;
    }

    section.teNewsletter = true;
    form.noValidate = true;
    form.setAttribute('data-nocaptcha', 'true');

    form.addEventListener('submit', function (event) {
      if (form.teSubmitNatively) {
        return;
      }

      event.preventDefault();

      if (!form.classList.contains('is-loading') && form.querySelector('[name="contact[email]"]')) {
        submitNewsletter(section, form);
      }
    });

    // The error goes away as the shopper corrects the address
    form.addEventListener('input', function (event) {
      if (event.target.name === 'contact[email]' && event.target.hasAttribute('aria-invalid')) {
        setNewsletterMessage(form, '');
      }
    });

    section.addEventListener('click', function (event) {
      var copyButton = event.target.closest('[data-te-newsletter-copy]');

      if (copyButton) {
        copyNewsletterCode(copyButton);
      }
    });
  }

  /* ---------------------------------------------------------------------------
     Category slider (sections/te-category-slider.liquid)

     The row is a native scroll-snap strip, so fingers and trackpads scroll it with the browser's own momentum. This
     adds the desktop arrows, dragging with a mouse, the progress line and the optional auto-slide. Every stop is a
     whole number of card widths (CSS lines the stops up with scroll-padding); the end of the row is the last stop.
     --------------------------------------------------------------------------- */

  var CAT_DRAG_THRESHOLD = 6; // Pixels a pressed mouse must move before it drags the row (less is a click)
  var CAT_FLICK_TIME = 200; // A mouse flick carries the row on by its speed (px/ms) times this
  var CAT_TOUCH_RESUME = 3000; // Auto-slide waits this long after a finger leaves the row
  var CAT_SCROLL_END = 120; // No scroll events for this long = the row has stopped
  var CAT_ON_SCREEN_RATIO = 0.3; // Share of the slider on screen before it auto-slides

  function catPrefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function catSliderStep(slider) {
    var cell = slider.track.firstElementChild;

    return cell ? cell.getBoundingClientRect().width : 0;
  }

  function catSliderMax(slider) {
    return Math.max(0, slider.track.scrollWidth - slider.track.clientWidth);
  }

  function scrollCatSlider(slider, left) {
    slider.target = Math.max(0, Math.min(catSliderMax(slider), left));

    // Already there (no scroll will happen): finish straight away
    if (Math.abs(slider.track.scrollLeft - slider.target) < 1) {
      onCatSliderScrollEnd(slider);
      return;
    }

    slider.track.scrollTo({ left: slider.target, behavior: catPrefersReducedMotion() ? 'auto' : 'smooth' });
  }

  // One card forwards or back. A click during a slide counts on from where that slide is heading, so fast clicks add up.
  function stepCatSlider(slider, direction) {
    var step = catSliderStep(slider);
    var from = slider.target !== null ? slider.target : slider.track.scrollLeft;

    if (!step) {
      return;
    }

    // 0.01 absorbs rounding, so a row sitting on a stop never counts as between two
    scrollCatSlider(slider, (direction > 0 ? Math.floor(from / step + 0.01) + 1 : Math.ceil(from / step - 0.01) - 1) * step);
  }

  function onCatSliderScrollEnd(slider) {
    slider.target = null;

    // A mouse drag has glided to its stop: snapping goes back on with the row already lined up, so nothing jumps
    if (slider.releasing) {
      slider.releasing = false;
      slider.track.classList.remove('is-dragging');
    }
  }

  function updateCatSlider(slider) {
    var track = slider.track;
    var max = catSliderMax(slider);
    var left = track.scrollLeft;
    var isStatic = max <= 1;
    var visible = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
    var progress = isStatic ? 0 : Math.min(1, Math.max(0, left / max));
    var media = track.querySelector('.te-cat-slider__media');

    slider.frame = null;

    // Every card fits: no arrows, progress line or auto-slide (CSS centres the row)
    if (isStatic !== slider.section.classList.contains('te-cat-slider--static')) {
      slider.section.classList.toggle('te-cat-slider--static', isStatic);
      scheduleCatSlider(slider);
    }

    slider.previousButton.disabled = isStatic || left <= 1;
    slider.nextButton.disabled = isStatic || left >= max - 1;

    // The dark part is the share of the row on screen, and slides along the line as the row moves
    slider.bar.style.width = (visible * 100) + '%';
    slider.bar.style.transform = 'translateX(' + (progress * (1 / visible - 1) * 100) + '%)';

    // Arrows sit halfway down the photos, whatever the image shape
    if (media) {
      slider.section.style.setProperty('--te-cat-arrow-top', (media.offsetHeight / 2) + 'px');
    }
  }

  function requestCatSliderUpdate(slider) {
    if (!slider.frame) {
      slider.frame = window.requestAnimationFrame(function () {
        updateCatSlider(slider);
      });
    }
  }

  // Auto-slide holds while the shopper is using or reading the row, and while it's off screen or the tab is hidden
  function isCatSliderHeld(slider) {
    return slider.hovered || slider.focused || slider.touching || slider.dragging || slider.editing || !slider.onScreen
      || document.hidden || slider.section.classList.contains('te-cat-slider--static');
  }

  // (Re)starts the auto-slide countdown, so the row never moves right after the shopper has moved it
  function scheduleCatSlider(slider) {
    clearTimeout(slider.autoplayTimer);
    slider.autoplayTimer = null;

    if (!slider.canAutoplay || isCatSliderHeld(slider)) {
      return;
    }

    slider.autoplayTimer = setTimeout(function () {
      slider.autoplayTimer = null;

      if (isCatSliderHeld(slider)) {
        return;
      }

      // At the end it glides back to the first card
      if (slider.track.scrollLeft >= catSliderMax(slider) - 1) {
        scrollCatSlider(slider, 0);
      } else {
        stepCatSlider(slider, 1);
      }

      scheduleCatSlider(slider);
    }, slider.speed);
  }

  // Mouse only: fingers and trackpads already scroll the row natively
  function watchCatSliderDrag(slider) {
    var track = slider.track;
    var drag = null;

    function endDrag(event) {
      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }

      var step = catSliderStep(slider);
      var wasMoved = drag.moved;
      var velocity = Math.max(-3, Math.min(3, drag.velocity));

      drag = null;
      slider.dragging = false;

      if (!wasMoved) {
        return;
      }

      // The click that ends a drag must not open the card
      slider.justDragged = true;
      setTimeout(function () {
        slider.justDragged = false;
      }, 0);

      // Glide to the nearest card (a flick carries on a little), with snapping still off
      slider.releasing = true;
      scrollCatSlider(slider, step ? Math.round((track.scrollLeft - velocity * CAT_FLICK_TIME) / step) * step : track.scrollLeft);
      scheduleCatSlider(slider);
    }

    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0 || slider.section.classList.contains('te-cat-slider--static')) {
        return;
      }

      drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startLeft: track.scrollLeft,
        lastX: event.clientX,
        lastTime: event.timeStamp,
        velocity: 0,
        moved: false
      };
    });

    track.addEventListener('pointermove', function (event) {
      if (!drag || event.pointerId !== drag.pointerId) {
        return;
      }

      var distance = event.clientX - drag.startX;
      var elapsed = event.timeStamp - drag.lastTime;

      if (!drag.moved) {
        if (Math.abs(distance) < CAT_DRAG_THRESHOLD) {
          return;
        }

        drag.moved = true;
        slider.dragging = true;
        slider.releasing = false;
        slider.target = null;
        track.classList.add('is-dragging');
        track.setPointerCapture(event.pointerId);

        if (window.getSelection) {
          window.getSelection().removeAllRanges();
        }

        scheduleCatSlider(slider);
      }

      // Smoothed pointer speed, for the flick on release
      if (elapsed > 0) {
        drag.velocity = 0.8 * ((event.clientX - drag.lastX) / elapsed) + 0.2 * drag.velocity;
        drag.lastX = event.clientX;
        drag.lastTime = event.timeStamp;
      }

      track.scrollLeft = drag.startLeft - distance;
    });

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('lostpointercapture', endDrag);

    track.addEventListener('click', function (event) {
      if (slider.justDragged) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);

    // No ghost image of a link or photo while dragging
    track.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });
  }

  function watchCatSliderHolds(slider) {
    var section = slider.section;
    var track = slider.track;

    function setHeld(name, value) {
      if (slider[name] !== value) {
        slider[name] = value;
        scheduleCatSlider(slider);
      }
    }

    slider.wrapper.addEventListener('pointerenter', function (event) {
      if (event.pointerType === 'mouse') {
        setHeld('hovered', true);
      }
    });

    slider.wrapper.addEventListener('pointerleave', function (event) {
      if (event.pointerType === 'mouse') {
        setHeld('hovered', false);
      }
    });

    // Keyboard focus only: a mouse click also focuses a card or arrow, and that shouldn't stop the row for good
    section.addEventListener('focusin', function (event) {
      var isKeyboard = true;

      try {
        isKeyboard = event.target.matches(':focus-visible');
      } catch (error) {
        // Older browsers: treat every focus as keyboard focus
      }

      setHeld('focused', isKeyboard);
    });

    section.addEventListener('focusout', function (event) {
      if (!event.relatedTarget || !section.contains(event.relatedTarget)) {
        setHeld('focused', false);
      }
    });

    track.addEventListener('touchstart', function () {
      clearTimeout(slider.touchTimer);
      slider.target = null;
      setHeld('touching', true);
    }, { passive: true });

    ['touchend', 'touchcancel'].forEach(function (type) {
      track.addEventListener(type, function () {
        clearTimeout(slider.touchTimer);
        slider.touchTimer = setTimeout(function () {
          setHeld('touching', false);
        }, CAT_TOUCH_RESUME);
      }, { passive: true });
    });

    // A trackpad swipe takes over from any slide in progress
    track.addEventListener('wheel', function () {
      slider.target = null;
    }, { passive: true });

    if ('IntersectionObserver' in window) {
      slider.onScreen = false;
      slider.intersectionObserver = new IntersectionObserver(function (entries) {
        setHeld('onScreen', entries[0].isIntersecting && entries[0].intersectionRatio >= CAT_ON_SCREEN_RATIO);
      }, { threshold: [0, CAT_ON_SCREEN_RATIO, 1] });

      slider.intersectionObserver.observe(slider.wrapper);
    }
  }

  function initCategorySlider(section) {
    var track = section.querySelector('[data-te-cat-track]');

    if (section.teCatSlider || !track) {
      return;
    }

    var slider = section.teCatSlider = {
      section: section,
      wrapper: track.parentNode,
      track: track,
      bar: section.querySelector('[data-te-cat-bar]'),
      previousButton: section.querySelector('[data-te-cat-prev]'),
      nextButton: section.querySelector('[data-te-cat-next]'),
      canAutoplay: section.getAttribute('data-autoplay') === 'true' && !catPrefersReducedMotion(),
      speed: Math.max(3, parseInt(section.getAttribute('data-speed'), 10) || 4) * 1000,
      target: null,
      frame: null,
      autoplayTimer: null,
      touchTimer: null,
      scrollEndTimer: null,
      resizeObserver: null,
      intersectionObserver: null,
      onResize: null,
      onScreen: true,
      hovered: false,
      focused: false,
      touching: false,
      dragging: false,
      releasing: false,
      editing: false,
      justDragged: false
    };

    track.addEventListener('scroll', function () {
      requestCatSliderUpdate(slider);

      clearTimeout(slider.scrollEndTimer);
      slider.scrollEndTimer = setTimeout(function () {
        onCatSliderScrollEnd(slider);
      }, CAT_SCROLL_END);
    }, { passive: true });

    slider.previousButton.addEventListener('click', function () {
      stepCatSlider(slider, -1);
      scheduleCatSlider(slider);
    });

    slider.nextButton.addEventListener('click', function () {
      stepCatSlider(slider, 1);
      scheduleCatSlider(slider);
    });

    if ('ResizeObserver' in window) {
      slider.resizeObserver = new ResizeObserver(function () {
        requestCatSliderUpdate(slider);
      });

      slider.resizeObserver.observe(track);
    } else {
      slider.onResize = function () {
        requestCatSliderUpdate(slider);
      };

      window.addEventListener('resize', slider.onResize);
    }

    watchCatSliderDrag(slider);
    watchCatSliderHolds(slider);

    section.classList.add('te-cat-slider--ready');
    updateCatSlider(slider);
    scheduleCatSlider(slider);
  }

  function destroyCategorySlider(section) {
    var slider = section.teCatSlider;

    if (!slider) {
      return;
    }

    clearTimeout(slider.autoplayTimer);
    clearTimeout(slider.touchTimer);
    clearTimeout(slider.scrollEndTimer);
    window.cancelAnimationFrame(slider.frame);

    if (slider.resizeObserver) {
      slider.resizeObserver.disconnect();
    } else {
      window.removeEventListener('resize', slider.onResize);
    }

    if (slider.intersectionObserver) {
      slider.intersectionObserver.disconnect();
    }

    delete section.teCatSlider;
  }

  /* ---------------------------------------------------------------------------
     Hero slideshow (sections/te-hero-slideshow.liquid)

     The slides are stacked and crossfade in CSS. This moves between them (arrows, dots, keyboard, swipes), runs the
     optional autoplay and loads each photo just before its slide is shown: only the first photo is in the HTML, so the
     others never slow it down.
     --------------------------------------------------------------------------- */

  var HERO_SWIPE_DISTANCE = 40; // Pixels a finger or mouse must travel sideways to change slide
  var HERO_TOUCH_RESUME = 3000; // Autoplay waits this long after a finger leaves the hero
  var HERO_ON_SCREEN_RATIO = 0.5; // Share of the hero on screen before it autoplays

  function loadHeroSlide(slide) {
    if (!slide || slide.teHeroLoaded) {
      return;
    }

    slide.teHeroLoaded = true;

    // The <source> first, so a large screen never starts on the phone photo
    slide.querySelectorAll('source[data-srcset]').forEach(function (source) {
      source.srcset = source.getAttribute('data-srcset');
      source.removeAttribute('data-srcset');
    });

    slide.querySelectorAll('img[data-srcset]').forEach(function (image) {
      image.srcset = image.getAttribute('data-srcset');
      image.src = image.getAttribute('data-src');
      image.removeAttribute('data-srcset');
      image.removeAttribute('data-src');
    });
  }

  // Autoplay holds while the shopper is using or reading the hero, and while it's off screen or the tab is hidden
  function isHeroHeld(hero) {
    return hero.hovered || hero.focused || hero.touching || hero.editing || !hero.onScreen || document.hidden;
  }

  // Runs or holds the countdown to the next slide. A hold keeps the time left, so the current dot's fill (paused by
  // .is-paused) and the countdown pick up again together.
  function updateHeroAutoplay(hero) {
    if (!hero.canAutoplay) {
      return;
    }

    // "Loop" off: the slideshow stops on the last slide (its dot shows full)
    var isAtEnd = !hero.loop && hero.index === hero.slides.length - 1;

    hero.section.classList.toggle('te-hero--at-end', isAtEnd);

    if (isAtEnd) {
      clearTimeout(hero.timer);
      hero.timer = null;
      hero.section.classList.remove('is-paused');
      hero.slidesElement.setAttribute('aria-live', 'polite');
      return;
    }

    var isHeld = isHeroHeld(hero);

    hero.section.classList.toggle('is-paused', isHeld);

    // Screen readers announce a slide the shopper changes, not one every few seconds
    hero.slidesElement.setAttribute('aria-live', isHeld ? 'polite' : 'off');

    if (isHeld) {
      if (hero.timer) {
        clearTimeout(hero.timer);
        hero.timer = null;
        hero.remaining = Math.max(0, hero.remaining - (Date.now() - hero.startedAt));
      }

      return;
    }

    if (!hero.timer) {
      hero.startedAt = Date.now();
      hero.timer = setTimeout(function () {
        hero.timer = null;
        goToHeroSlide(hero, hero.index + 1, 1);
      }, hero.remaining);
    }
  }

  // A new slide gets the full time (its dot starts filling from empty)
  function restartHeroAutoplay(hero) {
    clearTimeout(hero.timer);
    hero.timer = null;
    hero.remaining = hero.speed;
    updateHeroAutoplay(hero);
  }

  // With "Loop" off there is no previous slide on the first one, and no next one on the last
  function updateHeroArrows(hero) {
    var last = hero.slides.length - 1;

    if (hero.previousButton) {
      hero.previousButton.disabled = !hero.loop && hero.index === 0;
    }

    if (hero.nextButton) {
      hero.nextButton.disabled = !hero.loop && hero.index === last;
    }
  }

  // "Slide" transition: the new slide starts on the side the shopper is heading to, and the old one moves out the other
  // way for the length of the move (CSS animates both)
  function prepareHeroSlideMove(hero, incoming, outgoing, direction) {
    clearTimeout(incoming.teHeroLeaveTimer);
    incoming.classList.remove('is-leaving');
    incoming.style.transform = 'translateX(' + (direction * 100) + '%)';
    void incoming.offsetWidth; // Start position in place before it becomes the active slide

    clearTimeout(outgoing.teHeroLeaveTimer);
    outgoing.classList.add('is-leaving');
    outgoing.style.transform = 'translateX(' + (-direction * 100) + '%)';
    outgoing.teHeroLeaveTimer = setTimeout(function () {
      outgoing.classList.remove('is-leaving');
      outgoing.style.transform = '';
    }, hero.fadeMs + 50);
  }

  // Wraps around at both ends when "Loop" is on. direction: 1 = forwards, -1 = back (from the slide order if not given).
  function goToHeroSlide(hero, index, direction) {
    var count = hero.slides.length;

    if (!hero.loop && (index < 0 || index >= count)) {
      return;
    }

    var next = ((index % count) + count) % count;
    var current = hero.slides[hero.index];
    var hadFocus = current.contains(document.activeElement);
    var isSlideMove = hero.transition === 'slide' && !catPrefersReducedMotion();

    if (next === hero.index) {
      return;
    }

    if (isSlideMove) {
      prepareHeroSlideMove(hero, hero.slides[next], current, direction || (next > hero.index ? 1 : -1));
    }

    hero.slides.forEach(function (slide, slideIndex) {
      if (slideIndex === next) {
        slide.classList.add('is-active');
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('inert');
      } else {
        slide.classList.remove('is-active');
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('inert', '');
      }
    });

    hero.dots.forEach(function (dot, dotIndex) {
      dot.classList.toggle('is-active', dotIndex === next);

      if (dotIndex === next) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    // The new slide moves from its start position to the middle
    if (isSlideMove) {
      hero.slides[next].style.transform = '';
    }

    hero.index = next;
    updateHeroArrows(hero);

    // Arrows and dots take the new slide's text colour
    hero.section.setAttribute('data-scheme', hero.slides[next].getAttribute('data-scheme'));

    // This photo now, and the one after it ahead of time
    loadHeroSlide(hero.slides[next]);
    loadHeroSlide(hero.slides[(next + 1) % count]);

    // Keyboard focus in the slide that has gone moves to the new one, instead of being lost
    if (hadFocus) {
      var focusTarget = hero.slides[next].querySelector('a[href]') || hero.dots[next];

      if (focusTarget) {
        focusTarget.focus({ preventScroll: true });
      }
    }

    restartHeroAutoplay(hero);
  }

  // Fingers, pens and mice: a sideways move changes the slide as soon as it is long enough. Vertical moves still scroll
  // the page (touch-action: pan-y in CSS).
  function watchHeroSwipe(hero) {
    var area = hero.slidesElement;
    var swipe = null;

    function endSwipe(event) {
      if (!swipe || event.pointerId !== swipe.pointerId) {
        return;
      }

      // The click that ends a mouse swipe must not open the link under it
      if (swipe.isDone) {
        hero.justSwiped = true;
        setTimeout(function () {
          hero.justSwiped = false;
        }, 0);
      }

      swipe = null;
    }

    area.addEventListener('pointerdown', function (event) {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) {
        return;
      }

      swipe = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        isDone: false
      };
    });

    area.addEventListener('pointermove', function (event) {
      if (!swipe || swipe.isDone || event.pointerId !== swipe.pointerId) {
        return;
      }

      var distanceX = event.clientX - swipe.startX;
      var distanceY = event.clientY - swipe.startY;

      if (Math.abs(distanceX) < HERO_SWIPE_DISTANCE || Math.abs(distanceX) < Math.abs(distanceY) * 1.2) {
        return;
      }

      swipe.isDone = true;

      if (window.getSelection) {
        window.getSelection().removeAllRanges();
      }

      goToHeroSlide(hero, hero.index + (distanceX < 0 ? 1 : -1), distanceX < 0 ? 1 : -1);
    });

    area.addEventListener('pointerup', endSwipe);
    area.addEventListener('pointercancel', endSwipe);

    area.addEventListener('click', function (event) {
      if (hero.justSwiped) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);

    // No ghost image of a link or photo while swiping with a mouse
    area.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });
  }

  function watchHeroHolds(hero) {
    var section = hero.section;

    function setHeld(name, value) {
      if (hero[name] !== value) {
        hero[name] = value;
        updateHeroAutoplay(hero);
      }
    }

    // "Pause while the mouse is over the hero" (section setting)
    section.addEventListener('pointerenter', function (event) {
      if (event.pointerType === 'mouse' && hero.pauseOnHover) {
        setHeld('hovered', true);
      }
    });

    section.addEventListener('pointerleave', function (event) {
      if (event.pointerType === 'mouse') {
        setHeld('hovered', false);
      }
    });

    // Keyboard focus only: a mouse click also focuses an arrow or dot, and that shouldn't stop the autoplay for good
    section.addEventListener('focusin', function (event) {
      var isKeyboard = true;

      try {
        isKeyboard = event.target.matches(':focus-visible');
      } catch (error) {
        // Older browsers: treat every focus as keyboard focus
      }

      setHeld('focused', isKeyboard);
    });

    section.addEventListener('focusout', function (event) {
      if (!event.relatedTarget || !section.contains(event.relatedTarget)) {
        setHeld('focused', false);
      }
    });

    section.addEventListener('touchstart', function () {
      clearTimeout(hero.touchTimer);
      setHeld('touching', true);
    }, { passive: true });

    ['touchend', 'touchcancel'].forEach(function (type) {
      section.addEventListener(type, function () {
        clearTimeout(hero.touchTimer);
        hero.touchTimer = setTimeout(function () {
          setHeld('touching', false);
        }, HERO_TOUCH_RESUME);
      }, { passive: true });
    });

    // On screen = half the hero is showing, or it fills half the screen (a hero taller than a landscape phone's screen
    // can never be half visible)
    if ('IntersectionObserver' in window) {
      hero.onScreen = false;
      hero.intersectionObserver = new IntersectionObserver(function (entries) {
        var entry = entries[0];

        setHeld('onScreen', entry.isIntersecting && (entry.intersectionRatio >= HERO_ON_SCREEN_RATIO
          || entry.intersectionRect.height >= window.innerHeight * HERO_ON_SCREEN_RATIO));
      }, { threshold: [0, 0.1, 0.2, 0.3, 0.4, HERO_ON_SCREEN_RATIO, 0.6, 0.7, 0.8, 0.9, 1] });

      hero.intersectionObserver.observe(section);
    }
  }

  function initHeroSlideshow(section) {
    var slidesElement = section.querySelector('[data-te-hero-slides]');
    var slides = section.querySelectorAll('[data-te-hero-slide]');

    // One slide: nothing to move between
    if (section.teHero || !slidesElement || slides.length < 2) {
      return;
    }

    var speed = Math.max(3, parseInt(section.getAttribute('data-speed'), 10) || 6) * 1000;
    var previousButton = section.querySelector('[data-te-hero-prev]');
    var nextButton = section.querySelector('[data-te-hero-next]');

    // Section settings (sections/te-hero-slideshow.liquid)
    var hero = section.teHero = {
      section: section,
      slidesElement: slidesElement,
      slides: Array.prototype.slice.call(slides),
      dots: Array.prototype.slice.call(section.querySelectorAll('[data-te-hero-dot]')),
      previousButton: previousButton,
      nextButton: nextButton,
      index: 0,
      speed: speed,
      remaining: speed,
      startedAt: 0,
      canAutoplay: section.getAttribute('data-autoplay') === 'true' && !catPrefersReducedMotion(),
      pauseOnHover: section.getAttribute('data-pause-hover') !== 'false',
      transition: section.getAttribute('data-transition') === 'slide' ? 'slide' : 'fade',
      fadeMs: parseInt(section.getAttribute('data-fade-ms'), 10) || 900,
      loop: section.getAttribute('data-loop') !== 'false',
      timer: null,
      touchTimer: null,
      intersectionObserver: null,
      onScreen: true,
      hovered: false,
      focused: false,
      touching: false,
      editing: false,
      justSwiped: false
    };

    // Without autoplay (off, or reduced motion) the current dot is simply filled
    section.classList.toggle('te-hero--autoplay', hero.canAutoplay);
    section.style.setProperty('--te-hero-speed', speed + 'ms');

    if (previousButton) {
      previousButton.addEventListener('click', function () {
        goToHeroSlide(hero, hero.index - 1, -1);
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', function () {
        goToHeroSlide(hero, hero.index + 1, 1);
      });
    }

    hero.dots.forEach(function (dot, dotIndex) {
      dot.addEventListener('click', function () {
        goToHeroSlide(hero, dotIndex);
      });
    });

    section.addEventListener('keydown', function (event) {
      if ((event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }

      var direction = event.key === 'ArrowRight' ? 1 : -1;

      event.preventDefault();
      goToHeroSlide(hero, hero.index + direction, direction);
    });

    // "Swipe or drag to change slides" (section setting)
    if (section.getAttribute('data-swipe') !== 'false') {
      watchHeroSwipe(hero);
    }

    watchHeroHolds(hero);
    updateHeroArrows(hero);

    // The second photo loads once the page (and so the first photo) has finished loading
    if (document.readyState === 'complete') {
      loadHeroSlide(hero.slides[1]);
    } else {
      window.addEventListener('load', function () {
        loadHeroSlide(hero.slides[1]);
      });
    }

    section.classList.add('te-hero--ready');
    restartHeroAutoplay(hero);
  }

  function destroyHeroSlideshow(section) {
    var hero = section.teHero;

    if (!hero) {
      return;
    }

    clearTimeout(hero.timer);
    clearTimeout(hero.touchTimer);

    hero.slides.forEach(function (slide) {
      clearTimeout(slide.teHeroLeaveTimer);
    });

    if (hero.intersectionObserver) {
      hero.intersectionObserver.disconnect();
    }

    delete section.teHero;
  }

  /* ---------------------------------------------------------------------------
     Matcha bar (sections/te-matcha.liquid): the story and the product row each ease in once, the first time they
     scroll into view, and the round badge only turns while the section is on screen. Without IntersectionObserver, or
     with reduced motion, nothing is hidden.
     --------------------------------------------------------------------------- */

  function revealMatcha(section) {
    section.querySelectorAll('[data-te-matcha-group]').forEach(function (group) {
      group.classList.add('is-revealed');
    });
  }

  function initMatcha(section) {
    if (section.teMatcha || !('IntersectionObserver' in window)) {
      return;
    }

    var matcha = section.teMatcha = { observers: [] };

    if (!catPrefersReducedMotion()) {
      var revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.15 });

      section.querySelectorAll('[data-te-matcha-group]').forEach(function (group) {
        revealObserver.observe(group);
      });

      section.setAttribute('data-te-reveal', 'ready');
      matcha.observers.push(revealObserver);
    }

    var screenObserver = new IntersectionObserver(function (entries) {
      section.classList.toggle('is-offscreen', !entries[0].isIntersecting);
    });

    screenObserver.observe(section);
    matcha.observers.push(screenObserver);
  }

  function destroyMatcha(section) {
    if (section.teMatcha) {
      section.teMatcha.observers.forEach(function (observer) {
        observer.disconnect();
      });

      delete section.teMatcha;
    }
  }

  /* ---------------------------------------------------------------------------
     Matcha Bar page and Events page (templates/page.matcha-bar.liquid and page.events.liquid; sections/te-mb-*.liquid
     and te-ev-*.liquid, which share these parts): "Open now / Closed now" worked out in
     Dubai time, events whose date has passed hidden (the page may have been cached on an earlier day), the enquiry
     form (RSVP or question: RSVP shows the event and guests fields; the event list is filled from the events on the
     page) and the map that only loads when asked.
     --------------------------------------------------------------------------- */

  var MB_TIME_ZONE = 'Asia/Dubai';
  var MB_WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  var mbStatusTimer = null;

  // Today's date (YYYYMMDD as a number), minutes since midnight and weekday, in Dubai
  function mbDubaiNow() {
    var now = new Date();
    var parts = {};

    try {
      new Intl.DateTimeFormat('en-GB', {
        timeZone: MB_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        weekday: 'long'
      }).formatToParts(now).forEach(function (part) {
        parts[part.type] = part.value;
      });
    } catch (error) {
      // Dubai is UTC+4 all year round (no daylight saving)
      var dubai = new Date(now.getTime() + 4 * 3600000);

      parts = {
        year: String(dubai.getUTCFullYear()),
        month: ('0' + (dubai.getUTCMonth() + 1)).slice(-2),
        day: ('0' + dubai.getUTCDate()).slice(-2),
        hour: String(dubai.getUTCHours()),
        minute: String(dubai.getUTCMinutes()),
        weekday: MB_WEEKDAYS[dubai.getUTCDay()]
      };
    }

    return {
      date: parseInt(parts.year + parts.month + parts.day, 10),
      minutes: (parseInt(parts.hour, 10) % 24) * 60 + parseInt(parts.minute, 10),
      weekday: String(parts.weekday).toLowerCase()
    };
  }

  // "10:00" -> 600
  function mbMinutes(time) {
    var match = /^(\d{1,2})[:.](\d{2})$/.exec(String(time || '').trim());

    return match ? parseInt(match[1], 10) * 60 + parseInt(match[2], 10) : null;
  }

  function updateMbStatus(element) {
    var opens = mbMinutes(element.getAttribute('data-opens'));
    var closes = mbMinutes(element.getAttribute('data-closes'));

    if (opens === null || closes === null) {
      element.hidden = true;
      return;
    }

    var now = mbDubaiNow().minutes;
    // Hours past midnight (e.g. 10:00 to 01:00) wrap round
    var isOpen = closes > opens ? (now >= opens && now < closes) : (now >= opens || now < closes);

    element.textContent = element.getAttribute(isOpen ? 'data-open-label' : 'data-closed-label');
    element.classList.toggle('is-open', isOpen);
    element.classList.toggle('is-closed', !isOpen);
    element.hidden = false;
  }

  // Opening hours: the row for today ("Friday", "Fri...", "Every day", "Daily") is highlighted
  function highlightMbHours(list) {
    var today = mbDubaiNow().weekday;

    list.querySelectorAll('.te-mb-hours__row').forEach(function (row) {
      var label = row.querySelector('dt').textContent.trim().toLowerCase();

      row.classList.toggle('is-today', label.indexOf(today) !== -1 || label.indexOf(today.slice(0, 3)) === 0 || /every ?day|daily/.test(label));
    });
  }

  function initMbStatus() {
    document.querySelectorAll('[data-te-mb-status]').forEach(updateMbStatus);
    document.querySelectorAll('[data-te-mb-hours]').forEach(highlightMbHours);

    if (!mbStatusTimer && document.querySelector('[data-te-mb-status]')) {
      mbStatusTimer = setInterval(function () {
        document.querySelectorAll('[data-te-mb-status]').forEach(updateMbStatus);
      }, 60000);
    }
  }

  // The events section lists the upcoming events of the day it was rendered: hide any that have passed since
  function initMbEvents(section) {
    var today = mbDubaiNow().date;
    var upcoming = 0;
    var list = section.querySelector('[data-te-mb-events-list]');
    var empty = section.querySelector('[data-te-mb-events-empty]');

    section.querySelectorAll('[data-te-mb-event]').forEach(function (event) {
      var end = parseInt(event.getAttribute('data-end'), 10);

      if (end && end < today) {
        event.hidden = true;
      } else {
        upcoming += 1;
      }
    });

    if (list) {
      list.hidden = upcoming === 0;
    }

    if (empty) {
      empty.hidden = upcoming > 0;
    }
  }

  function mbEnquiryType(form) {
    var checked = form.querySelector('[data-te-mb-type]:checked');

    return checked ? checked.getAttribute('data-te-mb-type') : 'question';
  }

  // Only the fields for the chosen enquiry are shown (and sent)
  function updateMbEnquiry(form) {
    var type = mbEnquiryType(form);

    form.querySelectorAll('[data-te-mb-for]').forEach(function (group) {
      var show = group.getAttribute('data-te-mb-for').split(' ').indexOf(type) !== -1;

      group.hidden = !show;
      group.querySelectorAll('input, select, textarea').forEach(function (field) {
        field.disabled = !show;
      });
    });
  }

  function setMbEnquiry(form, type, eventName) {
    var radio = form.querySelector('[data-te-mb-type="' + type + '"]');
    var select = form.querySelector('[data-te-mb-event-select]');

    if (radio && !radio.closest('[hidden]')) {
      radio.checked = true;
    }

    if (select && eventName) {
      select.value = eventName;
    }

    updateMbEnquiry(form);
  }

  function initMbEnquiry(wrapper) {
    var form = wrapper.querySelector('form');
    var success = wrapper.querySelector('[data-te-mb-success]');

    if (wrapper.teMbEnquiry || !form) {
      return;
    }

    wrapper.teMbEnquiry = true;

    // After sending, the page opens at the form: move focus to the thank-you so it is read out. Done after the
    // browser has jumped to the form (#te-mb-enquiry-form), which would otherwise reset the focus.
    if (success) {
      var focusSuccess = function () {
        setTimeout(function () {
          success.focus({ preventScroll: true });
        }, 0);
      };

      if (document.readyState === 'complete') {
        focusSuccess();
      } else {
        window.addEventListener('load', focusSuccess);
      }

      return;
    }

    var select = form.querySelector('[data-te-mb-event-select]');
    var events = document.querySelectorAll('[data-te-mb-event][data-rsvp="true"]:not([hidden])');
    var rsvpChoice = form.querySelector('[data-te-mb-choice="rsvp"]');

    if (select) {
      events.forEach(function (event) {
        var option = document.createElement('option');

        option.value = event.getAttribute('data-name');
        option.textContent = event.getAttribute('data-name');
        select.appendChild(option);
      });
    }

    // Nothing to RSVP to: with only "question" left there is no choice to make, so the whole row is hidden
    if (rsvpChoice) {
      rsvpChoice.hidden = events.length === 0;
      rsvpChoice.closest('fieldset').hidden = events.length === 0;
    }

    form.addEventListener('change', function (event) {
      if (event.target.hasAttribute('data-te-mb-type')) {
        updateMbEnquiry(form);
      }
    });

    updateMbEnquiry(form);
  }

  /* ---------------------------------------------------------------------------
     Footer (sections/footer.liquid): on phones each column folds under its heading. The toggle button is hidden on
     larger screens, where every column stays open.
     --------------------------------------------------------------------------- */

  function setFooterColumnOpen(column, isOpen) {
    var toggle = column.querySelector('[data-te-footer-toggle]');

    column.classList.toggle('is-open', isOpen);

    if (toggle) {
      toggle.setAttribute('aria-expanded', String(isOpen));
    }
  }

  /* ---------------------------------------------------------------------------
     Featured collections (sections/featured-collections.liquid): from 1008px the carousel's arrows are Flickity's own
     buttons, styled in te-sections.css. This places them halfway down the photos, whatever their shape, and 16px off
     the cards when the side margin has room for that; a narrower margin puts them on the row's edge, half over the
     photos (as on Shop by category). Phones and tablets swipe, with dots. The product page's "You may also like"
     (sections/product-recommendations.liquid) has the same arrows.
     --------------------------------------------------------------------------- */

  var FC_ARROW_LISTS = '.te-fc .ProductList--carousel, .product-recommendation-section .ProductList--carousel';
  var FC_ARROW_SIZE = 45; // .RoundButton
  var FC_ARROW_GAP = 16; // between the arrow and the cards
  var FC_ARROW_EDGE = 8; // the least space left between the arrow and the screen edge

  var fcArrowObserver = null;

  function updateFeaturedCollectionArrows(list) {
    var buttons = list.querySelectorAll('.flickity-prev-next-button');
    var media = list.querySelector('.te-card__media, .ProductItem__ImageWrapper');
    var cell = list.querySelector('.Carousel__Cell');

    // No arrows (lightSlider below 1008px), or a tab that isn't open: it's measured when it opens
    if (!buttons.length || !media || !media.offsetHeight || !cell) {
      return;
    }

    buttons.forEach(function (button) {
      button.setAttribute('aria-label', button.classList.contains('previous') ? 'Previous products' : 'Next products');
    });

    var margin = parseFloat(getComputedStyle(list).paddingLeft) + parseFloat(getComputedStyle(cell).paddingLeft);
    var hasRoom = margin >= FC_ARROW_SIZE + FC_ARROW_GAP + FC_ARROW_EDGE;
    var mediaTop = media.getBoundingClientRect().top - list.getBoundingClientRect().top;

    list.style.setProperty('--te-fc-arrow-top', (mediaTop + media.offsetHeight / 2) + 'px');
    list.style.setProperty('--te-fc-arrow-x', (hasRoom ? margin - FC_ARROW_GAP - FC_ARROW_SIZE : margin - FC_ARROW_SIZE / 2) + 'px');
  }

  // The list changes size when the window does, when its photos get their shape, and when its tab opens
  function initFeaturedCollectionArrows(list) {
    if (list.teFcArrows) {
      return;
    }

    list.teFcArrows = true;

    if ('ResizeObserver' in window) {
      fcArrowObserver = fcArrowObserver || new ResizeObserver(function (entries) {
        entries.forEach(function (entry) {
          updateFeaturedCollectionArrows(entry.target);
        });
      });

      fcArrowObserver.observe(list);
    } else {
      window.addEventListener('resize', function () {
        updateFeaturedCollectionArrows(list);
      });

      updateFeaturedCollectionArrows(list);
    }
  }

  /* ---------------------------------------------------------------------------
     Product carousels (.ProductList--carousel: featured collections, recommendations, recently viewed). From 1008px
     Flickity runs them and turns itself on and off from the theme's CSS (watchCSS); below that lightSlider shows three
     products at a time on tablets and two on phones (under 700px), with dots. theme.js only started lightSlider when the page loaded, so a window that got narrower
     (a tablet turned upright, a resized browser) was left with neither and the cards stacked in one column. This builds
     and removes lightSlider whenever the width crosses 1008px. theme.js calls update() after it loads recommendations or
     recently viewed products.
     --------------------------------------------------------------------------- */

  var productCarouselQuery = window.matchMedia('(max-width: 1007px)');

  var PRODUCT_CAROUSEL_OPTIONS = {
    gallery: false,
    item: 3,
    responsive: [{ breakpoint: 700, settings: { item: 2 } }], // lightSlider applies it below 700px, also on resize
    speed: 500,
    auto: false,
    loop: false,
    pager: true,
    controls: false,
    slideMargin: -15,
    enableDrag: false
  };

  // A browser still holding the old theme.js (cached, or a page loaded while the files upload) also starts lightSlider
  // on every product carousel once the page is ready: a second slider inside the first, with a second row of dots.
  // A product carousel that already slides is skipped, whoever asks.
  if (window.jQuery && window.jQuery.fn.lightSlider) {
    var buildLightSlider = window.jQuery.fn.lightSlider;

    window.jQuery.fn.lightSlider = function (options) {
      return buildLightSlider.call(this.not('.ProductList--carousel.lightSlider'), options);
    };
  }

  /*
    Auto-slide, for the carousels whose section turns it on (data-te-autoplay, data-te-autoplay-speed in seconds). It
    moves whichever slider runs the list at the time (a page of products with Flickity, one product with lightSlider)
    and glides back to the start at the end. Like Shop by category, it holds while the shopper hovers, touches or tabs
    through the row, while the row is off screen, in a closed tab or a hidden browser tab, and never runs with reduced
    motion. When a hold ends the countdown starts again, so the row never moves right after the shopper has used it.
  */
  function isProductCarouselHeld(auto) {
    return auto.hovered || auto.focused || auto.touching || auto.editing || !auto.onScreen || document.hidden
      || !auto.list.offsetWidth;
  }

  function stepProductCarousel(list) {
    var flickity = window.Flickity && window.Flickity.data(list);

    if (list.teLightSlider) {
      var dots = list.parentNode.parentNode.querySelectorAll('.lSPager li');
      var current = Array.prototype.findIndex.call(dots, function (dot) {
        return dot.classList.contains('active');
      });

      // No dots: every product is already on screen
      if (dots.length < 2) {
        return;
      }

      if (current >= dots.length - 1) {
        list.teLightSlider.goToSlide(0);
      } else {
        list.teLightSlider.goToNextSlide();
      }
    } else if (flickity && flickity.isActive && flickity.slides.length > 1) {
      if (flickity.selectedIndex >= flickity.slides.length - 1) {
        flickity.select(0);
      } else {
        flickity.next();
      }
    }
  }

  function scheduleProductCarousel(auto) {
    clearTimeout(auto.timer);
    auto.timer = null;

    if (isProductCarouselHeld(auto)) {
      return;
    }

    auto.timer = setTimeout(function () {
      auto.timer = null;

      if (!isProductCarouselHeld(auto)) {
        stepProductCarousel(auto.list);
        scheduleProductCarousel(auto);
      }
    }, auto.speed);
  }

  function initProductCarouselAutoplay(list) {
    var wrapper = list.closest('.ProductListWrapper') || list.parentNode;

    if (list.teAutoplay || list.getAttribute('data-te-autoplay') !== 'true' || catPrefersReducedMotion()) {
      return;
    }

    var auto = list.teAutoplay = {
      list: list,
      speed: Math.max(3, parseInt(list.getAttribute('data-te-autoplay-speed'), 10) || 4) * 1000,
      timer: null,
      touchTimer: null,
      observer: null,
      onScreen: true,
      hovered: false,
      focused: false,
      touching: false,
      editing: false
    };

    function setHeld(name, value) {
      if (auto[name] !== value) {
        auto[name] = value;
        scheduleProductCarousel(auto);
      }
    }

    // The wrapper outlives both sliders (lightSlider wraps the list inside it), so these survive the switch at 1008px
    wrapper.addEventListener('pointerenter', function (event) {
      if (event.pointerType === 'mouse') {
        setHeld('hovered', true);
      }
    });

    wrapper.addEventListener('pointerleave', function (event) {
      if (event.pointerType === 'mouse') {
        setHeld('hovered', false);
      }
    });

    // Keyboard focus only: a click also focuses a card, and that shouldn't stop the row for good
    wrapper.addEventListener('focusin', function (event) {
      var isKeyboard = true;

      try {
        isKeyboard = event.target.matches(':focus-visible');
      } catch (error) {
        // Older browsers: treat every focus as keyboard focus
      }

      setHeld('focused', isKeyboard);
    });

    wrapper.addEventListener('focusout', function (event) {
      if (!event.relatedTarget || !wrapper.contains(event.relatedTarget)) {
        setHeld('focused', false);
      }
    });

    wrapper.addEventListener('touchstart', function () {
      clearTimeout(auto.touchTimer);
      setHeld('touching', true);
    }, { passive: true });

    ['touchend', 'touchcancel'].forEach(function (type) {
      wrapper.addEventListener(type, function () {
        clearTimeout(auto.touchTimer);
        auto.touchTimer = setTimeout(function () {
          setHeld('touching', false);
        }, CAT_TOUCH_RESUME);
      }, { passive: true });
    });

    // The wrapper, not the list: lightSlider's list is a strip several screens wide. Also fires when a closed tab opens.
    if ('IntersectionObserver' in window) {
      auto.onScreen = false;
      auto.observer = new IntersectionObserver(function (entries) {
        setHeld('onScreen', entries[0].isIntersecting && entries[0].intersectionRatio >= CAT_ON_SCREEN_RATIO);
      }, { threshold: [0, CAT_ON_SCREEN_RATIO, 1] });

      auto.observer.observe(wrapper);
    }

    scheduleProductCarousel(auto);
  }

  function destroyProductCarouselAutoplay(list) {
    var auto = list.teAutoplay;

    if (auto) {
      clearTimeout(auto.timer);
      clearTimeout(auto.touchTimer);

      if (auto.observer) {
        auto.observer.disconnect();
      }

      delete list.teAutoplay;
    }
  }

  function updateProductCarousel(list) {
    var flickity = window.Flickity && window.Flickity.data(list);

    initProductCarouselAutoplay(list);

    if (productCarouselQuery.matches) {
      if (list.teLightSlider || list.classList.contains('lightSlider') || !window.jQuery || !window.jQuery.fn.lightSlider) {
        return;
      }

      // Flickity only lets go on its own resize, 150ms later: its viewport has to leave before lightSlider reads the cells
      if (flickity) {
        flickity.deactivate();
      }

      list.teLightSlider = window.jQuery(list).lightSlider(PRODUCT_CAROUSEL_OPTIONS);
    } else if (list.teLightSlider) {
      list.teLightSlider.destroy();
      list.teLightSlider = null;

      if (flickity) {
        flickity.watchCSS();
      }
    }
  }

  function updateProductCarousels() {
    document.querySelectorAll('.ProductList--carousel').forEach(updateProductCarousel);

    // Recommendations loaded by theme.js arrive as a new list, after init() has run
    document.querySelectorAll(FC_ARROW_LISTS).forEach(function (list) {
      initFeaturedCollectionArrows(list);
      updateFeaturedCollectionArrows(list);
    });
  }

  window.teProductCarousels = { update: updateProductCarousels };

  if (productCarouselQuery.addEventListener) {
    productCarouselQuery.addEventListener('change', updateProductCarousels);
  } else {
    productCarouselQuery.addListener(updateProductCarousels); // Safari before 14
  }

  // Theme editor: a removed section's lightSlider would keep resizing its detached list, and its auto-slide ticking
  document.addEventListener('shopify:section:unload', function (event) {
    var wrapper = document.getElementById('shopify-section-' + event.detail.sectionId);

    if (wrapper) {
      wrapper.querySelectorAll('.ProductList--carousel').forEach(function (list) {
        destroyProductCarouselAutoplay(list);

        if (list.teLightSlider) {
          list.teLightSlider.destroy();
          list.teLightSlider = null;
        }
      });
    }
  });

  // Theme editor: a collection picked in the sidebar (its tab opens) holds still until it is deselected
  function setProductCarouselEditing(blockId, isEditing) {
    var panel = document.getElementById('block-' + blockId);
    var list = panel && panel.querySelector('.ProductList--carousel');

    if (list && list.teAutoplay) {
      list.teAutoplay.editing = isEditing;
      scheduleProductCarousel(list.teAutoplay);
    }
  }

  document.addEventListener('shopify:block:select', function (event) {
    setProductCarouselEditing(event.detail.blockId, true);
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    setProductCarouselEditing(event.detail.blockId, false);
  });

  // Auto-slide stops while the browser tab is hidden and picks up again when it's back
  document.addEventListener('visibilitychange', function () {
    document.querySelectorAll('.ProductList--carousel').forEach(function (list) {
      if (list.teAutoplay) {
        scheduleProductCarousel(list.teAutoplay);
      }
    });
  });

  /* ---------------------------------------------------------------------------
     Init
     --------------------------------------------------------------------------- */

  function init() {
    document.querySelectorAll('[data-te-sticky-atc]').forEach(initStickyAddToCart);
    document.querySelectorAll('[data-section-type="product"], [data-section-type="featured-product"]').forEach(initSoldOutOptionValues);
    document.querySelectorAll('[data-te-wishlist-page]').forEach(initWishlistPage);
    document.querySelectorAll('[data-te-video-reels]').forEach(initVideoReels);
    document.querySelectorAll('[data-te-cat-slider]').forEach(initCategorySlider);
    document.querySelectorAll('[data-te-ig-marquee]').forEach(initInstagramMarquee);
    document.querySelectorAll('[data-te-press]').forEach(initPressLogos);
    document.querySelectorAll('[data-te-announce]').forEach(initAnnouncementBar);
    document.querySelectorAll('[data-te-newsletter]').forEach(initNewsletter);
    document.querySelectorAll('[data-te-hero]').forEach(initHeroSlideshow);
    document.querySelectorAll('[data-te-matcha]').forEach(initMatcha);
    initMbStatus();
    document.querySelectorAll('[data-te-mb-events]').forEach(initMbEvents);
    document.querySelectorAll('[data-te-mb-enquiry]').forEach(initMbEnquiry);
    updateProductCarousels();
    updateWishlistButtons();
  }

  // Page-wide listeners, set once
  document.addEventListener('click', function (event) {
    var wishlistButton = event.target.closest('[data-te-wishlist-toggle]');
    var quickAddButton = event.target.closest('[data-te-quick-add]');
    var quickViewButton = event.target.closest('[data-te-quickview]');
    var opensNewTab = event.ctrlKey || event.metaKey || event.shiftKey;

    if (wishlistButton) {
      event.preventDefault();
      toggleWishlist(wishlistButton);
    } else if (quickAddButton) {
      event.preventDefault();
      quickAdd(quickAddButton);
    } else if (quickViewButton && !(opensNewTab && quickViewButton.hasAttribute('href'))) {
      if (openQuickView(quickViewButton)) {
        event.preventDefault();
      }
    }
  });

  // Quick view: start loading the product as soon as the shopper shows intent (the mouse resting on the button for
  // 100 ms, a finger touching it, keyboard focus), so the popup usually opens with the product already here
  var quickViewHoverTimer = null;

  document.addEventListener('pointerover', function (event) {
    if (event.pointerType !== 'mouse') {
      return;
    }

    var trigger = event.target.closest && event.target.closest('[data-te-quickview]');

    clearTimeout(quickViewHoverTimer);

    if (trigger) {
      quickViewHoverTimer = setTimeout(function () {
        prefetchQuickView(trigger);
      }, 100);
    }
  });

  document.addEventListener('touchstart', function (event) {
    prefetchQuickView(event.target.closest && event.target.closest('[data-te-quickview]'));
  }, { passive: true });

  document.addEventListener('focusin', function (event) {
    prefetchQuickView(event.target.closest && event.target.closest('[data-te-quickview]'));
  });

  // Video reels: the centre button or the video itself plays/pauses, the speaker button turns sound on/off. Flickity
  // cancels the click that ends a drag, so sliding the row never toggles a reel.
  document.addEventListener('click', function (event) {
    var card = event.target.closest('[data-te-reel]');
    var video = card && card.querySelector('[data-te-reel-video]');

    if (!video || event.defaultPrevented) {
      return;
    }

    if (event.target.closest('[data-te-reel-mute]')) {
      toggleReelSound(video);
    } else {
      toggleReel(video);
    }
  });

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-te-footer-toggle]');

    if (toggle) {
      var column = toggle.closest('.te-footer__col');

      setFooterColumnOpen(column, !column.classList.contains('is-open'));
    }
  });

  // Featured collections with two tabs: the heading's "View all" follows the open tab's collection, and aria-selected
  // follows the open tab (theme.js only moves the is-active class). Picking a block in the editor clicks its tab too.
  document.addEventListener('click', function (event) {
    var tab = event.target.closest('.te-fc [data-action="toggle-tab"]');

    if (!tab) {
      return;
    }

    var section = tab.closest('.te-fc');
    var link = section.querySelector('[data-te-heading-link]');
    var url = tab.getAttribute('data-collection-url');

    section.querySelectorAll('[data-action="toggle-tab"]').forEach(function (item) {
      item.setAttribute('aria-selected', item === tab ? 'true' : 'false');
    });

    if (link && url) {
      link.setAttribute('href', url);
    }
  });

  // Theme editor: stop watching a removed featured collections or recommendations section's carousels
  document.addEventListener('shopify:section:unload', function (event) {
    var wrapper = document.getElementById('shopify-section-' + event.detail.sectionId);

    if (wrapper && fcArrowObserver) {
      wrapper.querySelectorAll(FC_ARROW_LISTS).forEach(function (list) {
        fcArrowObserver.unobserve(list);
      });
    }
  });

  // Theme editor: a footer column picked in the sidebar opens, so it can be seen on the phone preview
  document.addEventListener('shopify:block:select', function (event) {
    var column = document.querySelector('.te-footer__col[data-block-id="' + event.detail.blockId + '"]');

    if (column) {
      setFooterColumnOpen(column, true);
    }
  });

  // Theme editor: clean up a removed video reels section, and slide to the reel picked in the sidebar
  document.addEventListener('shopify:section:unload', function (event) {
    var wrapper = document.getElementById('shopify-section-' + event.detail.sectionId);
    var section = wrapper && wrapper.querySelector('[data-te-video-reels]');

    if (section) {
      destroyVideoReels(section);
    }
  });

  document.addEventListener('shopify:block:select', function (event) {
    var cell = document.querySelector('[data-te-video-reels] [data-block-id="' + event.detail.blockId + '"]');
    var reels = cell && cell.closest('[data-te-video-reels]').teVideoReels;

    if (reels && reels.flickity) {
      reels.flickity.select(Math.min(reels.flickity.getCellElements().indexOf(cell), reels.lastIndex));
    }
  });

  // Theme editor: stop checking a removed Instagram marquee
  document.addEventListener('shopify:section:unload', function (event) {
    var wrapper = document.getElementById('shopify-section-' + event.detail.sectionId);
    var section = wrapper && wrapper.querySelector('[data-te-ig-marquee]');

    if (section) {
      destroyInstagramMarquee(section);
    }
  });

  // Theme editor: clean up a removed category slider. A card picked in the sidebar slides into view and the row holds
  // still until it is deselected.
  document.addEventListener('shopify:section:unload', function (event) {
    var wrapper = document.getElementById('shopify-section-' + event.detail.sectionId);
    var section = wrapper && wrapper.querySelector('[data-te-cat-slider]');

    if (section) {
      destroyCategorySlider(section);
    }
  });

  document.addEventListener('shopify:block:select', function (event) {
    var cell = document.querySelector('[data-te-cat-slider] [data-block-id="' + event.detail.blockId + '"]');
    var slider = cell && cell.closest('[data-te-cat-slider]').teCatSlider;

    if (slider) {
      slider.editing = true;
      scrollCatSlider(slider, Array.prototype.indexOf.call(slider.track.children, cell) * catSliderStep(slider));
      scheduleCatSlider(slider);
    }
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var wrapper = document.getElementById('shopify-section-' + event.detail.sectionId);
    var section = wrapper && wrapper.querySelector('[data-te-cat-slider]');

    if (section && section.teCatSlider) {
      section.teCatSlider.editing = false;
      scheduleCatSlider(section.teCatSlider);
    }
  });

  // Category slider: auto-slide stops while the browser tab is hidden and picks up again when it's back
  document.addEventListener('visibilitychange', function () {
    document.querySelectorAll('[data-te-cat-slider]').forEach(function (section) {
      if (section.teCatSlider) {
        scheduleCatSlider(section.teCatSlider);
      }
    });
  });

  // Theme editor: clean up a removed hero slideshow. A slide picked in the sidebar is shown and holds still until it is
  // deselected.
  document.addEventListener('shopify:section:unload', function (event) {
    var wrapper = document.getElementById('shopify-section-' + event.detail.sectionId);
    var section = wrapper && wrapper.querySelector('[data-te-hero]');

    if (section) {
      destroyHeroSlideshow(section);
    }
  });

  document.addEventListener('shopify:block:select', function (event) {
    var slide = document.querySelector('[data-te-hero-slide][data-block-id="' + event.detail.blockId + '"]');
    var hero = slide && slide.closest('[data-te-hero]').teHero;

    if (hero) {
      hero.editing = true;
      goToHeroSlide(hero, hero.slides.indexOf(slide));
      updateHeroAutoplay(hero);
    }
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var wrapper = document.getElementById('shopify-section-' + event.detail.sectionId);
    var section = wrapper && wrapper.querySelector('[data-te-hero]');

    if (section && section.teHero) {
      section.teHero.editing = false;
      updateHeroAutoplay(section.teHero);
    }
  });

  // Hero slideshow: autoplay stops while the browser tab is hidden and picks up again when it's back
  document.addEventListener('visibilitychange', function () {
    document.querySelectorAll('[data-te-hero]').forEach(function (section) {
      if (section.teHero) {
        updateHeroAutoplay(section.teHero);
      }
    });
  });

  // Matcha Bar page: links to a part of the page (#menu, #events, #enquire...). The theme's own handler for "#" links
  // (theme.js) only allows for the announcement bar, so the target ended up under the sticky header; this one runs
  // first (capture phase) and scrolls so the part starts below the header (scroll-margin-top in te-sections.css), then
  // moves the keyboard focus there. "RSVP" links also choose RSVP and the event in the form. A link to a part that
  // isn't on the page (a hidden section) does nothing, rather than reach the theme's handler, which would fail.
  document.addEventListener('click', function (event) {
    var link = event.target.closest('.te-mb-page a[href^="#"]');
    var target = link && link.getAttribute('href').length > 1 && document.getElementById(link.getAttribute('href').slice(1));
    var behavior = catPrefersReducedMotion() ? 'auto' : 'smooth';
    var form;
    var focusTarget;

    if (!link || link.getAttribute('href').length < 2 || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (!target) {
      return;
    }

    form = link.hasAttribute('data-te-mb-enquire') && target.querySelector('form');

    if (form && form.querySelector('[data-te-mb-type]')) {
      setMbEnquiry(form, link.getAttribute('data-type'), link.getAttribute('data-event'));
      focusTarget = form.querySelector('[data-te-mb-type]:checked');
    } else {
      focusTarget = target;

      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }
    }

    target.scrollIntoView({ behavior: behavior, block: 'start' });

    if (focusTarget) {
      focusTarget.focus({ preventScroll: true });
    }

    if (window.history && history.replaceState) {
      history.replaceState(null, '', '#' + target.id);
    }
  }, true);

  // Matcha Bar page: the Google map loads only when "Show map" is pressed
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-te-mb-map]');
    var map = button && button.closest('.te-mb-map');
    var frame;

    if (!map) {
      return;
    }

    frame = document.createElement('iframe');
    frame.className = 'te-mb-map__frame';
    frame.src = button.getAttribute('data-src');
    frame.title = button.getAttribute('data-title');
    frame.referrerPolicy = 'no-referrer-when-downgrade';
    frame.setAttribute('allowfullscreen', '');
    map.appendChild(frame);
    map.classList.add('is-loaded');
    frame.focus();
  });

  // Theme editor: clean up a removed matcha section; picking it or one of its drinks shows everything at once
  document.addEventListener('shopify:section:unload', function (event) {
    var wrapper = document.getElementById('shopify-section-' + event.detail.sectionId);
    var section = wrapper && wrapper.querySelector('[data-te-matcha]');

    if (section) {
      destroyMatcha(section);
    }
  });

  ['shopify:section:select', 'shopify:block:select'].forEach(function (type) {
    document.addEventListener(type, function (event) {
      var wrapper = document.getElementById('shopify-section-' + event.detail.sectionId);
      var section = wrapper && wrapper.querySelector('[data-te-matcha]');

      if (section) {
        revealMatcha(section);
      }
    });
  });

  // Theme editor: picking the announcement bar or one of its messages in the sidebar brings it back if it was closed.
  // Set before the listener below, so the bar is visible when the message is centred.
  function reopenAnnouncementBar(event) {
    var section = findMarquee(event.detail.sectionId);

    if (section && section.classList.contains('is-closed')) {
      section.classList.remove('is-closed');
      setAnnouncementBarHeight(section.offsetHeight);
    }
  }

  document.addEventListener('shopify:section:select', reopenAnnouncementBar);
  document.addEventListener('shopify:block:select', reopenAnnouncementBar);

  // Theme editor: a marquee row (Instagram photos, press logos, announcement bar) stops with the item picked in the
  // sidebar centred, and starts moving again when it is deselected
  document.addEventListener('shopify:block:select', function (event) {
    var section = findMarquee(event.detail.sectionId);
    var item = section && section.querySelector('[data-block-id="' + event.detail.blockId + '"]');

    if (item) {
      holdMarqueeAt(section, item);
    }
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var section = findMarquee(event.detail.sectionId);

    if (section) {
      section.classList.remove('is-editing');
    }
  });

  // Standard dropdown size style (snippets/product-form.liquid): the <select> drives the hidden option radios the theme
  // listens to, and follows them when they change another way (e.g. from the sticky bar)
  document.addEventListener('change', function (event) {
    var option = event.target.closest('.ProductForm__Option');
    var select = option && option.querySelector('[data-te-option-select]');

    if (!select) {
      return;
    }

    if (event.target === select) {
      var radio = findByValue(option.querySelectorAll('[type="radio"]'), function (element) { return element.value; }, select.value);

      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else if (event.target.type === 'radio' && event.target.checked) {
      select.value = event.target.value;
    }
  });

  // Keep every open tab in sync
  window.addEventListener('storage', function (event) {
    if (event.key === WISHLIST_STORAGE_KEY) {
      wishlistItems = readWishlist();
      onWishlistChanged();
    }
  });

  watchForNewWishlistButtons();
  watchQuickViewModal();

  init();
  document.addEventListener('shopify:section:load', init);
})();
